/**
 * Explicit resource representations exchanged by negotiated V2 peers.
 *
 * A resource is deliberately not inferred from its serialized size. The
 * discriminator is part of the wire contract so callers can decide whether a
 * browser URL or an inline value is acceptable before native does any work.
 */
class InvalidResourceRepresentationError extends Error {
    constructor(message = "Invalid resource representation") {
        super(message);
        this.name = "InvalidResourceRepresentationError";
    }
}
class UnsupportedResourceRepresentationError extends Error {
    constructor(message = "No requested resource representation is supported") {
        super(message);
        this.name = "UnsupportedResourceRepresentationError";
    }
}
const RESOURCE_KINDS = ["url", "base64"];
function assertSafeMetadata(value) {
    if (!value || typeof value !== "object") {
        throw new InvalidResourceRepresentationError();
    }
    const metadata = value;
    if (typeof metadata.mimeType !== "string" || metadata.mimeType.trim().length === 0) {
        throw new InvalidResourceRepresentationError("Resource MIME type is required");
    }
    for (const key of ["width", "height", "sizeBytes"]) {
        const candidate = metadata[key];
        if (candidate !== undefined &&
            (!Number.isSafeInteger(candidate) || candidate < 0)) {
            throw new InvalidResourceRepresentationError(`Resource ${key} must be a non-negative safe integer`);
        }
    }
}
/** Validate and return a copy of an ordered representation preference list. */
function validateResourceRequest(request) {
    if (!request || typeof request.resourceId !== "string" || request.resourceId.length === 0) {
        throw new InvalidResourceRepresentationError("Resource identifier is required");
    }
    if (!Array.isArray(request.representations) ||
        request.representations.length === 0 ||
        request.representations.some((kind) => !RESOURCE_KINDS.includes(kind)) ||
        new Set(request.representations).size !== request.representations.length) {
        throw new InvalidResourceRepresentationError("Resource representations must be a non-empty ordered list");
    }
    return {
        resourceId: request.resourceId,
        representations: [...request.representations],
    };
}
/** Decode the strict V2 tagged union. Legacy V1 shape is intentionally rejected. */
function decodeResourceRepresentation(value) {
    if (!value || typeof value !== "object") {
        throw new InvalidResourceRepresentationError();
    }
    const representation = value;
    const kind = representation.kind;
    if (kind !== "url" && kind !== "base64") {
        throw new InvalidResourceRepresentationError("Resource kind is invalid");
    }
    assertSafeMetadata(value);
    const hasUrl = Object.prototype.hasOwnProperty.call(representation, "url");
    const hasBase64 = Object.prototype.hasOwnProperty.call(representation, "base64");
    if (kind === "url") {
        if (!hasUrl || hasBase64 || typeof representation.url !== "string" || representation.url.length === 0) {
            throw new InvalidResourceRepresentationError("URL resource value is invalid");
        }
        return { ...representation };
    }
    if (!hasBase64 || hasUrl || typeof representation.base64 !== "string") {
        throw new InvalidResourceRepresentationError("Base64 resource value is invalid");
    }
    return { ...representation };
}
/** Decode the legacy untagged V1 Base64 shape at a version-gated boundary. */
function decodeLegacyBase64Resource(value) {
    if (!value || typeof value !== "object") {
        throw new InvalidResourceRepresentationError();
    }
    const legacy = value;
    if (Object.prototype.hasOwnProperty.call(legacy, "kind") || typeof legacy.base64 !== "string") {
        throw new InvalidResourceRepresentationError("Legacy Base64 resource value is invalid");
    }
    assertSafeMetadata(value);
    return { kind: "base64", ...legacy };
}

/**
 * Core protocol types for Bifrost RPC Framework
 */
/**
 * How long `initialize()` waits for the native handshake when the caller names no
 * deadline of its own.
 *
 * Sized to the host's own page deadline, not to how long a handshake usually takes.
 * The native host abandons the page and reports a timeout at 15s; a shorter bridge
 * deadline leaves a window where the shim can still arrive, the web side has already
 * rejected, and native is still waiting — so the handshake never completes even
 * though nothing is broken. Anything longer is wasted: by then the failure has
 * already been reported and seen.
 */
const DEFAULT_READY_TIMEOUT_MS = 15000;
/** Default 50 MiB payloads can need 401 chunks at UTF-8 scalar boundaries. */
const MAX_CHUNKS_PER_TRANSFER = 401;

function utf8Scalar(value, index) {
    const code = value.charCodeAt(index);
    if (code >= 0xd800 &&
        code <= 0xdbff &&
        index + 1 < value.length &&
        value.charCodeAt(index + 1) >= 0xdc00 &&
        value.charCodeAt(index + 1) <= 0xdfff) {
        return { bytes: 4, codeUnits: 2 };
    }
    return {
        bytes: code <= 0x7f ? 1 : code <= 0x7ff ? 2 : 3,
        codeUnits: 1,
    };
}
/** UTF-8 size without allocating an encoded copy of a potentially large message. */
function utf8ByteLength(value) {
    let bytes = 0;
    for (let index = 0; index < value.length;) {
        const scalar = utf8Scalar(value, index);
        bytes += scalar.bytes;
        index += scalar.codeUnits;
    }
    return bytes;
}
/** Maximum chunks a greedy scalar-safe UTF-8 split can require. */
function maxUtf8ChunksForByteLength(byteLength, chunkSize) {
    if (byteLength <= 0)
        return 0;
    if (chunkSize < 4)
        return Infinity;
    const minimumBytes = (chunks) => {
        if (chunks <= 1)
            return 1;
        if (chunkSize <= 7) {
            return (Math.ceil(chunks / 2) * (chunkSize - 3) + Math.floor(chunks / 2) * 4);
        }
        return (chunks - 1) * (chunkSize - 3) + 4;
    };
    let low = 1;
    let high = Math.ceil(byteLength / (chunkSize - 3)) + 1;
    while (low < high) {
        const middle = Math.ceil((low + high) / 2);
        if (minimumBytes(middle) <= byteLength)
            low = middle;
        else
            high = middle - 1;
    }
    return low;
}
const preparedMessages = new WeakMap();
/** Cache the bridge's deadline measurement for the immediately following send. */
function prepareMessage(message) {
    const serialized = JSON.stringify(message);
    const prepared = { serialized, sizeBytes: utf8ByteLength(serialized) };
    preparedMessages.set(message, prepared);
    return prepared.sizeBytes;
}
function assertValidChunkConfig(config) {
    const chunkSize = config.chunkSize ?? 128 * 1024;
    const maxTransferSize = config.maxTransferSize ?? 16 * 1024 * 1024;
    if (!Number.isInteger(chunkSize) || chunkSize <= 0) {
        throw new Error(`chunkSize must be a positive integer, was ${chunkSize}`);
    }
    if (!Number.isInteger(maxTransferSize) || maxTransferSize <= 0) {
        throw new Error(`maxTransferSize must be a positive integer, was ${maxTransferSize}`);
    }
    if (maxUtf8ChunksForByteLength(maxTransferSize, chunkSize) >
        MAX_CHUNKS_PER_TRANSFER) {
        throw new Error(`maxTransferSize ${maxTransferSize} requires more than ${MAX_CHUNKS_PER_TRANSFER} chunks at chunkSize ${chunkSize}`);
    }
}
/** How many chunks to push before handing the thread back to the event loop. */
const CHUNKS_PER_YIELD = 16;
/** Bound per-send promise and closure overhead that the byte limit cannot see. */
const MAX_PENDING_SENDS = 100;
const yieldToEventLoop = () => new Promise((resolve) => setTimeout(resolve, 0));
function splitIntoChunks(transferId, data, chunkSize, responseId) {
    return splitIntoChunksWithByteLength(transferId, data, chunkSize, utf8ByteLength(data), responseId);
}
function splitIntoChunksWithByteLength(transferId, data, chunkSize, dataBytes, responseId) {
    if (!transferId)
        throw new Error("transferId must not be empty");
    if (!data)
        throw new Error("data must not be empty");
    if (responseId === "")
        throw new Error("responseId must not be empty");
    // chunkSize reaches here from setChunkConfig, so it is caller-controlled.
    // Zero made `total` Infinity and looped forever on empty slices; a negative
    // value produced zero chunks and silently discarded the message.
    if (!Number.isInteger(chunkSize) || chunkSize <= 0) {
        throw new Error(`chunkSize must be a positive integer, was ${chunkSize}`);
    }
    const pieces = [];
    const push = (piece) => {
        if (pieces.length === MAX_CHUNKS_PER_TRANSFER) {
            throw new Error("TOO_MANY_CHUNKS");
        }
        pieces.push(piece);
    };
    // JSON and base64 are normally ASCII. Keep that dominant path at one slice
    // per chunk rather than scanning every code point on the WebView thread.
    if (dataBytes === data.length) {
        for (let start = 0; start < data.length; start += chunkSize) {
            push(data.slice(start, start + chunkSize));
        }
    }
    else {
        let start = 0;
        while (start < data.length) {
            let end = start;
            let bytes = 0;
            while (end < data.length) {
                const scalar = utf8Scalar(data, end);
                if (scalar.bytes > chunkSize) {
                    throw new Error(`chunkSize must fit one UTF-8 character, was ${chunkSize}`);
                }
                if (bytes + scalar.bytes > chunkSize)
                    break;
                bytes += scalar.bytes;
                end += scalar.codeUnits;
            }
            push(data.slice(start, end));
            start = end;
        }
    }
    return pieces.map((piece, index) => ({
        type: "bifrost_chunk",
        transferId,
        ...(responseId ? { responseId } : {}),
        index,
        total: pieces.length,
        data: piece,
    }));
}
let transferCounter = 0;
class ChunkSender {
    constructor(config) {
        this.pendingBytes = 0;
        this.pendingSends = 0;
        assertValidChunkConfig(config);
        this.chunkThreshold = config.chunkThreshold ?? 256 * 1024;
        this.chunkSize = config.chunkSize ?? 128 * 1024;
        this.maxTransferSize = config.maxTransferSize ?? 16 * 1024 * 1024;
        this.send = config.send;
    }
    setConfig(config) {
        assertValidChunkConfig(config);
        this.chunkThreshold = config.chunkThreshold ?? 256 * 1024;
        this.chunkSize = config.chunkSize ?? 128 * 1024;
        this.maxTransferSize = config.maxTransferSize ?? 16 * 1024 * 1024;
    }
    async sendMessage(message, responseId, onStart, shouldSend, startSend) {
        const prepared = preparedMessages.get(message);
        preparedMessages.delete(message);
        const serialized = prepared?.serialized ?? JSON.stringify(message);
        const sizeBytes = prepared?.sizeBytes ?? utf8ByteLength(serialized);
        const chunkSize = this.chunkSize;
        const maxTransferSize = this.maxTransferSize;
        // Checked ahead of the threshold shortcut: the cap is on the message, not on
        // the chunked path, and a chunkThreshold above maxTransferSize would
        // otherwise let an oversized message through unchunked and unchecked.
        if (sizeBytes > maxTransferSize) {
            throw new Error("MESSAGE_TOO_LARGE");
        }
        // Keep one full-size message active and one waiting, matching the
        // receiver's default aggregate headroom without retaining an unbounded
        // promise chain of serialized payloads.
        if (this.pendingSends >= MAX_PENDING_SENDS ||
            this.pendingBytes + sizeBytes > maxTransferSize * 2) {
            throw new Error("OUTBOUND_QUEUE_FULL");
        }
        this.pendingBytes += sizeBytes;
        this.pendingSends++;
        const send = async () => {
            if (shouldSend && !shouldSend())
                return;
            const session = startSend?.();
            const chunkThreshold = session?.chunkThreshold ?? this.chunkThreshold;
            const sendFrame = session?.send ?? this.send;
            onStart?.(chunkThreshold);
            if (sizeBytes <= chunkThreshold) {
                await sendFrame(message, serialized);
                return;
            }
            const transferId = `tx_${++transferCounter}_${Date.now()}`;
            const chunks = splitIntoChunksWithByteLength(transferId, serialized, chunkSize, sizeBytes, responseId);
            for (let i = 0; i < chunks.length; i++) {
                if (shouldSend && !shouldSend())
                    return;
                await sendFrame(chunks[i]);
                // Awaiting a synchronous send only drains microtasks, so without an
                // explicit macrotask yield a large transfer holds the thread from first
                // chunk to last: no paint, no touch handling, no timers for its whole
                // duration. Yielding periodically rather than every chunk keeps the cost
                // proportionate.
                if ((i + 1) % CHUNKS_PER_YIELD === 0 && i + 1 < chunks.length) {
                    await yieldToEventLoop();
                }
            }
        };
        // Start an idle send immediately, preserving synchronous native delivery
        // for the common small-message path. Only overlapping sends queue behind
        // the active one so multipart transfers remain ordered and contiguous.
        let result;
        let tail;
        if (this.sendTail) {
            result = this.sendTail.then(send);
            tail = result.catch(() => { });
            this.sendTail = tail;
        }
        else {
            let release;
            const active = new Promise((resolve) => {
                release = resolve;
            });
            this.sendTail = active;
            result = send();
            void result.then(release, release);
            // A synchronous native callback may already have queued work behind the
            // active marker. Do not overwrite that newer tail.
            if (this.sendTail === active) {
                tail = result.catch(() => { });
                this.sendTail = tail;
            }
        }
        try {
            await result;
        }
        finally {
            this.pendingBytes -= sizeBytes;
            this.pendingSends--;
            if (tail && this.sendTail === tail)
                this.sendTail = undefined;
        }
    }
}

const DEFAULT_TRANSFER_TIMEOUT_MS = 30000;
const DEFAULT_MAX_TRANSFER_BYTES = 16 * 1024 * 1024;
const MAX_TRANSFER_LIFETIME_MS = 5 * 60000;
function isValidBifrostChunk(chunk) {
    return (chunk?.type === "bifrost_chunk" &&
        typeof chunk.transferId === "string" &&
        chunk.transferId.length > 0 &&
        (chunk.responseId === undefined ||
            (typeof chunk.responseId === "string" && chunk.responseId.length > 0)) &&
        typeof chunk.data === "string" &&
        chunk.data.length > 0 &&
        Number.isInteger(chunk.total) &&
        chunk.total > 0 &&
        chunk.total <= MAX_CHUNKS_PER_TRANSFER &&
        Number.isInteger(chunk.index) &&
        chunk.index >= 0 &&
        chunk.index < chunk.total);
}
class ChunkAssembler {
    constructor(config = {}) {
        this.transfers = new Map();
        this.bufferedBytes = 0;
        this.transferTimeoutMs =
            config.transferTimeoutMs ?? DEFAULT_TRANSFER_TIMEOUT_MS;
        this.maxConcurrentTransfers = config.maxConcurrentTransfers ?? 4;
        this.maxTransferBytes =
            config.maxTransferBytes ?? DEFAULT_MAX_TRANSFER_BYTES;
        // Headroom over one max-size transfer so a single large message does not
        // sit exactly on the ceiling and evict all concurrent small traffic.
        this.maxTotalBytes = config.maxTotalBytes ?? 32 * 1024 * 1024;
        this.onReject = config.onReject;
        this.onAccept = config.onAccept;
    }
    addChunk(chunk) {
        const transferId = typeof chunk?.transferId === "string" ? chunk.transferId : "";
        // Validate before allocating: an envelope the peer got wrong must not cost a
        // transfer slot or a byte of buffer. `Number.isInteger` also rejects the
        // string-index variant ("0"), which would otherwise slip past the duplicate
        // guard below because Map keys by identity.
        if (!isValidBifrostChunk(chunk)) {
            this.onReject?.(transferId, "malformed");
            return null;
        }
        const { index, total, data } = chunk;
        let transfer = this.transfers.get(transferId);
        let dataBytes;
        if (transfer &&
            (transfer.total !== total || transfer.responseId !== chunk.responseId)) {
            // The peer contradicted its own metadata mid-transfer. Nothing it has
            // sent for this transfer can be trusted, so drop the whole thing rather
            // than complete or extend whichever call happened to arrive first.
            this.discard(transferId);
            this.onReject?.(transferId, "malformed");
            return null;
        }
        if (!transfer) {
            if (this.transfers.size >= this.maxConcurrentTransfers) {
                this.onReject?.(transferId, "at-capacity");
                return null;
            }
            const metadataBytes = utf8ByteLength(transferId) +
                (chunk.responseId ? utf8ByteLength(chunk.responseId) : 0);
            dataBytes = utf8ByteLength(data);
            if (dataBytes > this.maxTransferBytes) {
                this.onReject?.(transferId, "too-large");
                return null;
            }
            if (metadataBytes + dataBytes > this.maxTotalBytes) {
                this.onReject?.(transferId, "at-capacity");
                return null;
            }
            if (this.bufferedBytes + metadataBytes > this.maxTotalBytes) {
                this.evictLargestUntil(this.maxTotalBytes - metadataBytes, transferId);
                if (this.bufferedBytes + metadataBytes > this.maxTotalBytes) {
                    this.onReject?.(transferId, "at-capacity");
                    return null;
                }
            }
            transfer = {
                chunks: new Map(),
                total,
                responseId: chunk.responseId,
                receivedCount: 0,
                bytes: 0,
                metadataBytes,
                createdAt: performance.now(),
                lastProgressAt: performance.now(),
            };
            this.transfers.set(transferId, transfer);
            this.bufferedBytes += metadataBytes;
        }
        if (transfer.chunks.has(index)) {
            if (transfer.chunks.get(index) !== data) {
                this.discard(transferId);
                this.onReject?.(transferId, "malformed");
            }
            return null; // An exact duplicate retransmit is benign.
        }
        if (dataBytes === undefined) {
            dataBytes = utf8ByteLength(data);
        }
        // Distinguish the two ceilings: exceeding maxTransferBytes means this
        // message is genuinely oversized, while exceeding maxTotalBytes means the
        // shared budget is exhausted by other traffic and says nothing about this
        // message's size.
        if (transfer.bytes + dataBytes > this.maxTransferBytes) {
            this.discard(transferId);
            this.onReject?.(transferId, "too-large");
            return null;
        }
        if (transfer.metadataBytes + transfer.bytes + dataBytes >
            this.maxTotalBytes) {
            this.discard(transferId);
            this.onReject?.(transferId, "at-capacity");
            return null;
        }
        if (this.bufferedBytes + dataBytes > this.maxTotalBytes) {
            // Make room by dropping the biggest other transfer first. Discarding the
            // arriving one instead punishes whichever transfer happened to cross the
            // line and throws away its progress, while the transfer actually holding
            // the memory survives.
            this.evictLargestUntil(this.maxTotalBytes - dataBytes, transferId);
            if (this.bufferedBytes + dataBytes > this.maxTotalBytes) {
                this.discard(transferId);
                this.onReject?.(transferId, "at-capacity");
                return null;
            }
        }
        transfer.chunks.set(index, data);
        transfer.bytes += dataBytes;
        this.bufferedBytes += dataBytes;
        transfer.receivedCount++;
        transfer.lastProgressAt = performance.now();
        this.scheduleTimeout(transferId, transfer);
        // Indices are validated into 0..total-1 and de-duplicated, so a full count
        // means every slot is filled. assemble() therefore cannot hit a hole, which
        // join() would otherwise coerce to "" and emit as a truncated message.
        const assembled = transfer.receivedCount === transfer.total
            ? this.assemble(transferId, transfer)
            : null;
        // Acceptance observers may synchronously re-enter or throw. Complete every
        // state transition first and keep observer failures outside the transport.
        try {
            this.onAccept?.(chunk);
        }
        catch {
            // Observer failures cannot un-accept a validated chunk.
        }
        return assembled;
    }
    getPendingTransferCount() {
        return this.transfers.size;
    }
    /** Bytes currently held across all in-flight transfers. */
    getBufferedBytes() {
        return this.bufferedBytes;
    }
    destroy() {
        for (const [, transfer] of this.transfers) {
            if (transfer.timeoutHandle) {
                clearTimeout(transfer.timeoutHandle);
            }
        }
        this.transfers.clear();
        this.bufferedBytes = 0;
    }
    /** Drops the largest transfers, other than `keep`, until `budget` is free. */
    evictLargestUntil(budget, keep) {
        while (this.bufferedBytes > budget) {
            let victim;
            let victimBytes = -1;
            for (const [id, t] of this.transfers) {
                const retainedBytes = t.bytes + t.metadataBytes;
                if (id !== keep && retainedBytes > victimBytes) {
                    victim = id;
                    victimBytes = retainedBytes;
                }
            }
            if (victim === undefined)
                return;
            this.discard(victim);
            this.onReject?.(victim, "at-capacity");
        }
    }
    discard(transferId) {
        const transfer = this.transfers.get(transferId);
        if (!transfer)
            return;
        if (transfer.timeoutHandle) {
            clearTimeout(transfer.timeoutHandle);
        }
        this.bufferedBytes -= transfer.bytes + transfer.metadataBytes;
        this.transfers.delete(transferId);
    }
    assemble(transferId, transfer) {
        if (transfer.timeoutHandle) {
            clearTimeout(transfer.timeoutHandle);
        }
        const parts = new Array(transfer.total);
        for (let i = 0; i < transfer.total; i++) {
            parts[i] = transfer.chunks.get(i);
        }
        transfer.chunks.clear();
        this.bufferedBytes -= transfer.bytes + transfer.metadataBytes;
        this.transfers.delete(transferId);
        return parts.join("");
    }
    scheduleTimeout(transferId, transfer) {
        if (transfer.timeoutHandle)
            clearTimeout(transfer.timeoutHandle);
        const now = performance.now();
        const delay = Math.max(0, Math.min(transfer.lastProgressAt + this.transferTimeoutMs - now, transfer.createdAt + MAX_TRANSFER_LIFETIME_MS - now));
        transfer.timeoutHandle = setTimeout(() => {
            if (this.transfers.get(transferId) !== transfer)
                return;
            const current = performance.now();
            if (current - transfer.lastProgressAt < this.transferTimeoutMs &&
                current - transfer.createdAt < MAX_TRANSFER_LIFETIME_MS) {
                this.scheduleTimeout(transferId, transfer);
                return;
            }
            this.discard(transferId);
            this.onReject?.(transferId, "expired");
        }, delay);
    }
}

/**
 * Shared data model types for Bifrost RPC communication.
 *
 * These types mirror Kotlin @Serializable data classes in the
 * `com.seekrtech.stforestkit.web.bifrost.types` package, providing
 * type-safe contracts for common payloads exchanged over the bridge.
 */
/**
 * Convert a BifrostImage to a data URL for use in `<img src="...">`.
 */
function bifrostImageToDataUrl(image) {
    return `data:${image.mimeType};base64,${image.base64}`;
}

/**
 * Error definitions and error codes for Bifrost RPC Framework
 */
/**
 * Error code ranges
 */
var ErrorCategory;
(function (ErrorCategory) {
    ErrorCategory[ErrorCategory["TRANSPORT"] = 1000] = "TRANSPORT";
    ErrorCategory[ErrorCategory["PROTOCOL"] = 2000] = "PROTOCOL";
    ErrorCategory[ErrorCategory["SERVICE"] = 3000] = "SERVICE";
    ErrorCategory[ErrorCategory["APPLICATION"] = 4000] = "APPLICATION";
    ErrorCategory[ErrorCategory["INTERNAL"] = 5000] = "INTERNAL";
})(ErrorCategory || (ErrorCategory = {}));
/**
 * Standard error codes
 */
var ErrorCode;
(function (ErrorCode) {
    // Transport errors (1000-1999)
    ErrorCode[ErrorCode["CONNECTION_FAILED"] = 1001] = "CONNECTION_FAILED";
    ErrorCode[ErrorCode["CONNECTION_TIMEOUT"] = 1002] = "CONNECTION_TIMEOUT";
    ErrorCode[ErrorCode["MESSAGE_TOO_LARGE"] = 1003] = "MESSAGE_TOO_LARGE";
    ErrorCode[ErrorCode["TOO_MANY_INFLIGHT"] = 1004] = "TOO_MANY_INFLIGHT";
    // Protocol errors (2000-2999)
    ErrorCode[ErrorCode["INVALID_REQUEST"] = 2001] = "INVALID_REQUEST";
    ErrorCode[ErrorCode["METHOD_NOT_FOUND"] = 2002] = "METHOD_NOT_FOUND";
    ErrorCode[ErrorCode["INVALID_PARAMS"] = 2003] = "INVALID_PARAMS";
    // Service errors (3000-3999)
    ErrorCode[ErrorCode["SERVICE_NOT_FOUND"] = 3001] = "SERVICE_NOT_FOUND";
    ErrorCode[ErrorCode["SERVICE_ERROR"] = 3002] = "SERVICE_ERROR";
    ErrorCode[ErrorCode["SERVICE_TIMEOUT"] = 3003] = "SERVICE_TIMEOUT";
    // Application errors (4000-4999)
    ErrorCode[ErrorCode["UNAUTHORIZED"] = 4001] = "UNAUTHORIZED";
    ErrorCode[ErrorCode["FORBIDDEN"] = 4002] = "FORBIDDEN";
    ErrorCode[ErrorCode["NOT_FOUND"] = 4003] = "NOT_FOUND";
    ErrorCode[ErrorCode["CONFLICT"] = 4004] = "CONFLICT";
    ErrorCode[ErrorCode["VALIDATION_ERROR"] = 4005] = "VALIDATION_ERROR";
    // Internal errors (5000-5999)
    ErrorCode[ErrorCode["INTERNAL_ERROR"] = 5000] = "INTERNAL_ERROR";
    ErrorCode[ErrorCode["NOT_IMPLEMENTED"] = 5001] = "NOT_IMPLEMENTED";
    ErrorCode[ErrorCode["NETWORK_ERROR"] = 1005] = "NETWORK_ERROR";
    ErrorCode[ErrorCode["TIMEOUT_ERROR"] = 1006] = "TIMEOUT_ERROR";
    ErrorCode[ErrorCode["SERVICE_UNAVAILABLE"] = 3004] = "SERVICE_UNAVAILABLE";
})(ErrorCode || (ErrorCode = {}));
/**
 * Custom RPC call error
 */
class RpcCallError extends Error {
    constructor(rpcError) {
        super(rpcError.message);
        this.rpcError = rpcError;
        this.name = "RpcCallError";
        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, RpcCallError);
        }
    }
    get code() {
        return this.rpcError.code;
    }
    get data() {
        return this.rpcError.data;
    }
}
class DeliveryUnknownError extends Error {
    constructor(message = "Call delivery is unknown", retryable = false) {
        super(message);
        this.retryable = retryable;
        this.name = "DeliveryUnknownError";
    }
}
class TransportNotAdmittedError extends Error {
    constructor(message = "Call was not admitted") {
        super(message);
        this.name = "TransportNotAdmittedError";
    }
}
class OverloadedError extends Error {
    constructor(message = "V2 call admission queue is full") {
        super(message);
        this.name = "OverloadedError";
    }
}
class CallDeadlineExceededError extends Error {
    constructor(message = "Call deadline expired before transport admission") {
        super(message);
        this.name = "CallDeadlineExceededError";
    }
}
class CallCancelledError extends Error {
    constructor(message = "Call cancelled") {
        super(message);
        this.name = "CallCancelledError";
    }
}
class PayloadTooLargeError extends Error {
    constructor(message = "V2 payload exceeds negotiated limits") {
        super(message);
        this.name = "PayloadTooLargeError";
    }
}
class SessionReplacedError extends Error {
    constructor(message = "V2 control session was replaced") {
        super(message);
        this.name = "SessionReplacedError";
    }
}

/**
 * Connection state management for Bifrost
 */
class ConnectionState {
    constructor(timeout) {
        this.timeout = timeout;
        this.ready = false;
        this.readyPromise = new Promise((resolve) => {
            this.readyResolve = resolve;
        });
    }
    /**
     * Ensure connection is ready, with timeout
     */
    async ensureReady() {
        if (this.ready) {
            return Promise.resolve();
        }
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error("Connection timeout")), this.timeout);
        });
        return Promise.race([this.readyPromise, timeoutPromise]);
    }
    /**
     * Mark connection as ready
     */
    setReady() {
        if (!this.ready) {
            this.ready = true;
            this.readyResolve();
        }
    }
    /**
     * Check if connection is ready
     */
    isReady() {
        return this.ready;
    }
    /**
     * Reset connection state (for testing/reconnection)
     */
    reset() {
        this.ready = false;
        this.readyPromise = new Promise((resolve) => {
            this.readyResolve = resolve;
        });
    }
}

/**
 * Shared symbol used to tag a thrown error with a call-scoped retry boundary,
 * so BifrostWeb can attach retry-wait behaviour that RetryManager later reads
 * off the error without either module needing to import the other.
 */
const CALL_RETRY_BOUNDARY = Symbol.for("@seekrtech/bifrost/call-retry-boundary");

/**
 * Retry logic manager for Bifrost operations
 */
class RetryManager {
    constructor(config) {
        this.config = {
            maxRetries: config.maxRetries ?? 3,
            retryDelay: config.retryDelay ?? 1000,
            retryBackoff: config.retryBackoff ?? 2,
            retryableErrors: config.retryableErrors ?? [
                ErrorCode.CONNECTION_FAILED,
                ErrorCode.CONNECTION_TIMEOUT,
                ErrorCode.INTERNAL_ERROR,
            ],
        };
    }
    /**
     * Execute operation with retry logic
     */
    async withRetry(operation) {
        let lastError;
        let attempt = 0;
        while (attempt <= this.config.maxRetries) {
            try {
                return await operation();
            }
            catch (error) {
                lastError = error;
                // Don't retry on last attempt or if error is not retryable
                if (attempt === this.config.maxRetries ||
                    !this.isRetryableError(error)) {
                    throw error;
                }
                // Calculate delay with exponential backoff
                const delay = this.calculateDelay(attempt);
                const boundary = error[CALL_RETRY_BOUNDARY];
                if (boundary) {
                    await boundary.waitBeforeRetry(delay);
                }
                else {
                    await this.sleep(delay);
                }
                attempt++;
            }
        }
        throw lastError;
    }
    /**
     * Check if error is retryable
     */
    isRetryableError(error) {
        if (error instanceof TransportNotAdmittedError)
            return true;
        if (error instanceof DeliveryUnknownError)
            return error.retryable;
        if (error instanceof RpcCallError) {
            return this.config.retryableErrors.includes(error.code);
        }
        // Generic errors are not retryable by default
        return false;
    }
    /**
     * Calculate delay with exponential backoff
     */
    calculateDelay(attempt) {
        return this.config.retryDelay * Math.pow(this.config.retryBackoff, attempt);
    }
    /**
     * Sleep utility
     */
    sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
    /**
     * Get current retry configuration
     */
    getConfig() {
        return { ...this.config };
    }
    /**
     * Check if specific error code is retryable
     */
    isErrorCodeRetryable(code) {
        return this.config.retryableErrors.includes(code);
    }
}

/**
 * Service validation utilities
 */
class ServiceValidator {
    /**
     * Validate service implementation
     */
    static validateService(name, implementation) {
        this.validateServiceName(name);
        this.validateServiceImplementation(implementation);
        this.validateServiceMethods(implementation);
    }
    /**
     * Validate service name format
     */
    static validateServiceName(name) {
        if (!name || name.trim() === "") {
            throw new Error("Service name cannot be empty");
        }
        if (!this.SERVICE_NAME_REGEX.test(name)) {
            if (/^[0-9]/.test(name)) {
                throw new Error("Service name must start with a letter");
            }
            throw new Error("Service name contains invalid characters");
        }
    }
    /**
     * Validate service implementation object
     */
    static validateServiceImplementation(implementation) {
        if (implementation === null || implementation === undefined) {
            throw new Error("Service implementation cannot be null or undefined");
        }
        if (typeof implementation !== "object" || Array.isArray(implementation)) {
            throw new Error("Service implementation must be an object");
        }
    }
    /**
     * Validate service methods
     */
    static validateServiceMethods(implementation) {
        const methods = this.getServiceMethods(implementation);
        if (methods.length === 0) {
            throw new Error("Service must have at least one method");
        }
        // Validate each method
        for (const methodName of methods) {
            this.validateMethodName(methodName);
            this.validateMethodSignature(implementation[methodName], methodName);
        }
    }
    /**
     * Validate method name
     */
    static validateMethodName(methodName) {
        if (this.RESERVED_METHOD_NAMES.includes(methodName)) {
            throw new Error(`Method name is reserved: ${methodName}`);
        }
        // Method names should follow similar rules as service names
        if (!this.SERVICE_NAME_REGEX.test(methodName)) {
            throw new Error(`Invalid method name: ${methodName}`);
        }
    }
    /**
     * Validate method signature
     */
    static validateMethodSignature(method, methodName) {
        const paramCount = method.length;
        // Methods should accept 0-2 parameters: (payload?, context?)
        if (paramCount > 2) {
            throw new Error(`Method ${methodName} has too many parameters (expected 0-2, got ${paramCount})`);
        }
    }
    /**
     * Get list of method names from service
     */
    static getServiceMethods(implementation) {
        const methods = [];
        for (const prop in implementation) {
            if (typeof implementation[prop] === "function") {
                methods.push(prop);
            }
        }
        return methods.sort(); // Return sorted for consistent ordering
    }
    /**
     * Check if a method name is reserved
     */
    static isReservedMethodName(methodName) {
        return this.RESERVED_METHOD_NAMES.includes(methodName);
    }
    /**
     * Validate service name format (public method)
     */
    static isValidServiceName(name) {
        try {
            this.validateServiceName(name);
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * Get validation errors without throwing
     */
    static getValidationErrors(name, implementation) {
        const errors = [];
        try {
            this.validateServiceName(name);
        }
        catch (error) {
            errors.push(error.message);
        }
        try {
            this.validateServiceImplementation(implementation);
        }
        catch (error) {
            errors.push(error.message);
            return errors; // Can't continue without valid implementation
        }
        try {
            this.validateServiceMethods(implementation);
        }
        catch (error) {
            errors.push(error.message);
        }
        return errors;
    }
}
ServiceValidator.RESERVED_METHOD_NAMES = [
    "constructor",
    "prototype",
    "toString",
    "valueOf",
    "hasOwnProperty",
    "isPrototypeOf",
    "propertyIsEnumerable",
    "__proto__",
    "__defineGetter__",
    "__defineSetter__",
    "__lookupGetter__",
    "__lookupSetter__",
];
ServiceValidator.SERVICE_NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/**
 * Service registry for managing RPC services
 */
class ServiceRegistry {
    constructor() {
        this.services = new Map();
    }
    /**
     * Register a service implementation
     */
    register(name, implementation) {
        if (this.services.has(name)) {
            throw new Error(`Service already registered: ${name}`);
        }
        // Use ServiceValidator for comprehensive validation
        ServiceValidator.validateService(name, implementation);
        this.services.set(name, implementation);
    }
    /**
     * Unregister a service
     */
    unregister(name) {
        this.services.delete(name);
    }
    /**
     * Invoke a service method
     */
    async invoke(service, method, payload) {
        const impl = this.services.get(service);
        if (!impl) {
            throw new RpcCallError({
                code: ErrorCode.SERVICE_NOT_FOUND,
                message: `Service not found: ${service}`,
            });
        }
        const fn = impl[method];
        if (typeof fn !== "function") {
            throw new RpcCallError({
                code: ErrorCode.METHOD_NOT_FOUND,
                message: `Method not found: ${service}.${method}`,
            });
        }
        try {
            // Create context for service call
            const context = {};
            // Call method with payload and context
            const result = await fn.call(impl, payload, context);
            return result;
        }
        catch (error) {
            // Re-throw RpcCallError as-is
            if (error instanceof RpcCallError) {
                throw error;
            }
            // Wrap other errors in RpcCallError
            throw new RpcCallError({
                code: ErrorCode.SERVICE_ERROR,
                message: error instanceof Error ? error.message : "Unknown service error",
                data: error,
            });
        }
    }
    /**
     * List all registered service names
     */
    listServices() {
        return Array.from(this.services.keys());
    }
    /**
     * Get service descriptor with method information
     */
    getServiceDescriptor(serviceName) {
        const impl = this.services.get(serviceName);
        if (!impl) {
            throw new Error(`Service not found: ${serviceName}`);
        }
        const methodNames = ServiceValidator.getServiceMethods(impl);
        const methods = methodNames.map((name) => ({
            name,
            // TODO: Add parameter introspection in future version
            params: [],
            returns: "any",
        }));
        return {
            name: serviceName,
            methods,
        };
    }
    /**
     * Check if service has a specific method
     */
    hasMethod(serviceName, methodName) {
        const impl = this.services.get(serviceName);
        return impl && typeof impl[methodName] === "function";
    }
    /**
     * Clear all registered services (useful for testing)
     */
    clear() {
        this.services.clear();
    }
    /**
     * Get the number of registered services
     */
    size() {
        return this.services.size;
    }
}

/**
 * InterceptorChain - Manages and executes interceptors in order
 */
class InterceptorChain {
    constructor() {
        this.interceptors = [];
    }
    /**
     * Add an interceptor to the chain
     */
    addInterceptor(interceptor) {
        // Remove existing interceptor with same name
        this.removeInterceptor(interceptor.name);
        // Add new interceptor and sort by order
        this.interceptors.push(interceptor);
        this.interceptors.sort((a, b) => a.order - b.order);
    }
    /**
     * Remove interceptor by name
     */
    removeInterceptor(name) {
        this.interceptors = this.interceptors.filter((i) => i.name !== name);
    }
    /**
     * Clear all interceptors
     */
    clearInterceptors() {
        this.interceptors = [];
    }
    /**
     * Get all interceptors
     */
    getInterceptors() {
        return [...this.interceptors];
    }
    /**
     * Process a message through the interceptor chain
     */
    async process(direction, message, finalHandler) {
        if (this.interceptors.length === 0) {
            return finalHandler();
        }
        let currentIndex = 0;
        const next = async () => {
            if (currentIndex >= this.interceptors.length) {
                return finalHandler();
            }
            const interceptor = this.interceptors[currentIndex++];
            return interceptor.intercept(direction, message, next);
        };
        return next();
    }
}

/**
 * NativeProxyManager - Advanced proxy creation with caching and validation
 */
/**
 * Enhanced native proxy manager with caching, validation, and TypeScript type safety
 */
class NativeProxyManager {
    constructor(bifrostWeb) {
        this.bifrostWeb = bifrostWeb;
        this.proxyCache = new Map();
        this.proxyMetadata = new Map();
        this.isDestroyed = false;
    }
    /**
     * Create a typed native proxy with caching and validation
     */
    createProxy(serviceName, options = {}) {
        this.throwIfDestroyed();
        this.validateServiceName(serviceName);
        // Return cached proxy if it exists
        if (this.proxyCache.has(serviceName)) {
            return this.proxyCache.get(serviceName);
        }
        // Create new proxy with enhanced features
        const proxy = this.createEnhancedProxy(serviceName, options);
        // Cache the proxy
        this.proxyCache.set(serviceName, proxy);
        // Store metadata
        this.proxyMetadata.set(serviceName, {
            serviceName,
            createdAt: new Date(),
            callCount: 0,
            options: { ...options },
            defaultCallOptions: options.defaultCallOptions,
            methodOptions: options.methodOptions,
        });
        return proxy;
    }
    /**
     * Get metadata for a cached proxy
     */
    getProxyMetadata(serviceName) {
        return this.proxyMetadata.get(serviceName);
    }
    /**
     * List all cached proxy service names
     */
    listCachedProxies() {
        return Array.from(this.proxyCache.keys());
    }
    /**
     * Clear specific proxy from cache
     */
    clearProxy(serviceName) {
        this.proxyCache.delete(serviceName);
        this.proxyMetadata.delete(serviceName);
    }
    /**
     * Clear all proxies from cache
     */
    clearAllProxies() {
        this.proxyCache.clear();
        this.proxyMetadata.clear();
    }
    /**
     * Destroy the proxy manager and cleanup resources
     */
    destroy() {
        this.clearAllProxies();
        this.isDestroyed = true;
    }
    /**
     * Create enhanced proxy with advanced features
     */
    createEnhancedProxy(serviceName, options) {
        const handler = {
            get: (_target, prop) => {
                if (typeof prop !== "string") {
                    return undefined;
                }
                return (...args) => {
                    // Get metadata and increment call count
                    const metadata = this.proxyMetadata.get(serviceName);
                    if (metadata) {
                        metadata.callCount++;
                    }
                    // Extract options from last parameter if present
                    let callOptions;
                    let methodArgs = args;
                    // Check if last argument looks like options
                    if (args.length > 0 &&
                        this.isOptionsParameter(args[args.length - 1])) {
                        callOptions = args[args.length - 1];
                        methodArgs = args.slice(0, -1);
                    }
                    // Determine payload - single arg becomes the payload, multiple args become array
                    let payload;
                    if (methodArgs.length === 0) {
                        payload = undefined;
                    }
                    else if (methodArgs.length === 1) {
                        payload = methodArgs[0];
                    }
                    else {
                        payload = methodArgs;
                    }
                    // Validate calls if enabled
                    if (options.validateCalls) {
                        this.validateMethodCall(serviceName, prop, payload);
                    }
                    // Get proxy-level default options from metadata
                    const proxyCallOptions = metadata?.defaultCallOptions;
                    // Get method-specific options from metadata
                    const methodCallOptions = metadata?.methodOptions?.[prop];
                    // Merge options: global → proxy → method → call
                    // BifrostWeb.mergeCallOptions handles global + proxy + method + call merging
                    const mergedOptions = this.bifrostWeb.mergeCallOptions(proxyCallOptions, methodCallOptions, callOptions);
                    // Determine if this is a notification (void method) or call (promise method)
                    // For now, we'll treat all methods as calls unless specifically configured
                    // In a real implementation, this could be inferred from TypeScript types
                    if (this.isNotificationMethod(prop, options)) {
                        this.bifrostWeb.notify(serviceName, prop, payload);
                        return; // void return for notifications
                    }
                    else {
                        // Return promise for calls with merged options
                        return this.bifrostWeb.call(serviceName, prop, payload, mergedOptions);
                    }
                };
            },
        };
        // Create proxy with empty target object
        return new Proxy({}, handler);
    }
    /**
     * Validate service name format
     */
    validateServiceName(serviceName) {
        if (!serviceName || typeof serviceName !== "string") {
            throw new Error(`Invalid service name: ${serviceName}`);
        }
        // Service name must start with letter and contain only letters, numbers, underscore
        const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;
        if (!validNamePattern.test(serviceName)) {
            throw new Error(`Invalid service name: ${serviceName}`);
        }
    }
    /**
     * Check if parameter looks like call options
     */
    isOptionsParameter(param) {
        return (param &&
            typeof param === "object" &&
            !Array.isArray(param) &&
            (param.timeout !== undefined ||
                param.retryConfig !== undefined ||
                param.binary !== undefined));
    }
    /**
     * Validate method call (placeholder for enhanced validation)
     */
    validateMethodCall(serviceName, method, _payload) {
        // Basic validation - could be enhanced with schema validation
        if (!method || typeof method !== "string") {
            throw new Error(`Invalid method name for service ${serviceName}: ${method}`);
        }
        // Additional validations could include:
        // - Parameter type checking
        // - Schema validation
        // - Service method existence checking
    }
    /**
     * Determine if method should be treated as notification
     * This is a placeholder - in practice could use TypeScript reflection or configuration
     */
    isNotificationMethod(method, _options) {
        // Simple heuristic: methods named 'notify' or ending in 'Notify' are notifications
        return (method === "notify" ||
            method.endsWith("Notify") ||
            method.startsWith("notify"));
    }
    /**
     * Throw error if proxy manager has been destroyed
     */
    throwIfDestroyed() {
        if (this.isDestroyed) {
            throw new Error("NativeProxyManager has been destroyed");
        }
    }
}

/**
 * Built-in request and response interceptors
 */
/**
 * Request interceptor for modifying outbound requests
 */
class RequestInterceptor {
    constructor() {
        this.name = "RequestInterceptor";
        this.order = 100;
        this.headers = {};
    }
    /**
     * Add a header to all outbound requests
     */
    addRequestHeader(key, value) {
        this.headers[key] = value;
    }
    /**
     * Remove a header from outbound requests
     */
    removeRequestHeader(key) {
        delete this.headers[key];
    }
    async intercept(direction, message, next) {
        if (direction === "outbound" && this.isRpcEnvelope(message)) {
            // Add headers to outbound requests
            if (Object.keys(this.headers).length > 0) {
                message.headers = {
                    ...message.headers,
                    ...this.headers,
                };
            }
        }
        return next();
    }
    isRpcEnvelope(message) {
        return "kind" in message;
    }
}
/**
 * Response interceptor for modifying inbound responses
 */
class ResponseInterceptor {
    constructor() {
        this.name = "ResponseInterceptor";
        this.order = 200;
        this.responseTransforms = [];
    }
    /**
     * Add a response transformation function
     */
    addResponseTransform(transform) {
        this.responseTransforms.push(transform);
    }
    /**
     * Clear all response transformations
     */
    clearResponseTransforms() {
        this.responseTransforms = [];
    }
    async intercept(direction, message, next) {
        const result = await next();
        if (direction === "inbound" && this.isRpcResponse(message)) {
            // Apply response transformations
            let transformedResponse = message;
            for (const transform of this.responseTransforms) {
                transformedResponse = transform(transformedResponse);
            }
            return transformedResponse;
        }
        return result;
    }
    isRpcResponse(message) {
        return "ok" in message;
    }
}
/**
 * Timing interceptor for measuring request performance
 */
class TimingInterceptor {
    constructor() {
        this.name = "TimingInterceptor";
        this.order = 10; // Run early to capture full timing
    }
    async intercept(direction, message, next) {
        if (direction === "outbound" && this.isRpcEnvelope(message)) {
            const startTime = performance.now();
            const result = await next();
            const endTime = performance.now();
            const duration = endTime - startTime;
            // Add timing data to the message
            message.timing = {
                startTime,
                endTime,
                duration,
            };
            return result;
        }
        return next();
    }
    isRpcEnvelope(message) {
        return "kind" in message;
    }
}
/**
 * Validation interceptor for ensuring message integrity
 */
class ValidationInterceptor {
    constructor() {
        this.name = "ValidationInterceptor";
        this.order = 1; // Run first to catch issues early
        this.customRules = {
            inbound: [],
            outbound: [],
        };
    }
    /**
     * Add a custom validation rule
     */
    addValidationRule(direction, rule) {
        this.customRules[direction].push(rule);
    }
    /**
     * Clear all validation rules for a direction
     */
    clearValidationRules(direction) {
        this.customRules[direction] = [];
    }
    async intercept(direction, message, next) {
        // Validate message structure
        if (direction === "outbound" && this.isRpcEnvelope(message)) {
            this.validateOutboundRequest(message);
        }
        else if (direction === "inbound" && this.isRpcResponse(message)) {
            this.validateInboundResponse(message);
        }
        // Apply custom validation rules
        for (const rule of this.customRules[direction]) {
            rule(message);
        }
        return next();
    }
    validateOutboundRequest(envelope) {
        if (!envelope.id) {
            throw new Error("Invalid request: missing id");
        }
        if (!envelope.kind) {
            throw new Error("Invalid request: missing kind");
        }
        if (envelope.kind === "call" && !envelope.service) {
            throw new Error("Invalid request: missing service");
        }
        if (envelope.kind === "call" && !envelope.method) {
            throw new Error("Invalid request: missing method");
        }
        if (!envelope.ts) {
            throw new Error("Invalid request: missing timestamp");
        }
    }
    validateInboundResponse(response) {
        if (!response.id) {
            throw new Error("Invalid response: missing id");
        }
        if (typeof response.ok !== "boolean") {
            throw new Error("Invalid response: missing ok field");
        }
        if (!response.ts) {
            throw new Error("Invalid response: missing timestamp");
        }
    }
    isRpcEnvelope(message) {
        return "kind" in message;
    }
    isRpcResponse(message) {
        return !("kind" in message);
    }
}

/**
 * Logging interceptor for debugging and monitoring RPC calls
 */
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 1] = "INFO";
    LogLevel[LogLevel["WARN"] = 2] = "WARN";
    LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
    LogLevel[LogLevel["NONE"] = 4] = "NONE";
})(LogLevel || (LogLevel = {}));
class LoggingInterceptor {
    constructor() {
        this.name = "LoggingInterceptor";
        this.order = 1000; // Run last to see final state
        this.logLevel = LogLevel.INFO;
        this.enabled = true;
        this.sensitiveFields = [];
        this.logger = console;
    }
    /**
     * Set the minimum log level
     */
    setLogLevel(level) {
        this.logLevel = level;
    }
    /**
     * Enable or disable logging
     */
    setEnabled(enabled) {
        this.enabled = enabled;
    }
    /**
     * Set fields that should be redacted in logs
     */
    setSensitiveFields(fields) {
        this.sensitiveFields = fields;
    }
    /**
     * Set a custom logger implementation
     */
    setCustomLogger(logger) {
        this.logger = logger;
    }
    async intercept(direction, message, next) {
        if (!this.enabled) {
            return next();
        }
        // Log the message before processing
        this.logMessage(direction, message);
        return next();
    }
    logMessage(direction, message) {
        if (this.isRpcEnvelope(message)) {
            this.logRequest(direction, message);
        }
        else {
            this.logResponse(direction, message);
        }
    }
    logRequest(direction, envelope) {
        const logData = this.sanitizeMessage(envelope);
        if (envelope.kind === "notify") {
            this.log(LogLevel.INFO, `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC notification:`, logData);
        }
        else {
            this.log(LogLevel.INFO, `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC call:`, logData);
        }
    }
    logResponse(direction, response) {
        const logData = this.sanitizeMessage(response);
        if (response.error) {
            this.log(LogLevel.ERROR, `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC error:`, logData);
        }
        else {
            this.log(LogLevel.INFO, `[Bifrost] ${direction === "outbound" ? "Outbound" : "Inbound"} RPC response:`, logData);
        }
    }
    sanitizeMessage(message) {
        if (this.sensitiveFields.length === 0) {
            return message;
        }
        const sanitized = { ...message };
        for (const field of this.sensitiveFields) {
            if (field in sanitized) {
                sanitized[field] = "[REDACTED]";
            }
        }
        return sanitized;
    }
    log(level, message, ...args) {
        if (level < this.logLevel) {
            return;
        }
        switch (level) {
            case LogLevel.DEBUG:
                this.logger.debug(message, ...args);
                break;
            case LogLevel.INFO:
                this.logger.info(message, ...args);
                break;
            case LogLevel.WARN:
                this.logger.warn(message, ...args);
                break;
            case LogLevel.ERROR:
                this.logger.error(message, ...args);
                break;
        }
    }
    isRpcEnvelope(message) {
        return "kind" in message;
    }
}

/**
 * Authentication interceptor for securing RPC calls
 */
class AuthInterceptor {
    constructor(authProvider) {
        this.name = "AuthInterceptor";
        this.order = 50; // Run after validation but before logging
        this.requiredAuthServices = new Set();
        this.excludedServices = new Set();
        this.requiredAuthMethods = new Map();
        this.authHeaderFormat = (token) => `Bearer ${token}`;
        this.authProvider = authProvider;
    }
    /**
     * Require authentication for a specific service
     */
    requireAuthForService(serviceName) {
        this.requiredAuthServices.add(serviceName);
    }
    /**
     * Exclude a service from authentication
     */
    excludeService(serviceName) {
        this.excludedServices.add(serviceName);
    }
    /**
     * Require authentication for a specific method
     */
    requireAuthForMethod(serviceName, methodName) {
        if (!this.requiredAuthMethods.has(serviceName)) {
            this.requiredAuthMethods.set(serviceName, new Set());
        }
        this.requiredAuthMethods.get(serviceName).add(methodName);
    }
    /**
     * Set custom auth header format
     */
    setAuthHeaderFormat(formatter) {
        this.authHeaderFormat = formatter;
    }
    async intercept(direction, message, next) {
        if (direction === "outbound" && this.isRpcEnvelope(message)) {
            await this.handleOutboundRequest(message);
        }
        else if (direction === "inbound" && this.isRpcResponse(message)) {
            this.handleInboundResponse(message);
        }
        return next();
    }
    async handleOutboundRequest(envelope) {
        // Skip notifications
        if (envelope.kind === "notify") {
            return;
        }
        // Skip excluded services
        if (envelope.service && this.excludedServices.has(envelope.service)) {
            return;
        }
        // Check if auth is required
        const requiresAuth = this.requiresAuthentication(envelope);
        // Get token
        let token = await this.authProvider.getToken();
        // If we have a token, validate it
        if (token && !this.authProvider.isTokenValid(token)) {
            token = await this.authProvider.refreshToken();
        }
        // Check if token is required but not available
        if (requiresAuth && !token) {
            if (envelope.method &&
                this.requiredAuthMethods.get(envelope.service)?.has(envelope.method)) {
                throw new Error(`Authentication required for method: ${envelope.service}.${envelope.method}`);
            }
            else {
                throw new Error(`Authentication required for service: ${envelope.service}`);
            }
        }
        // Add auth header if token is available
        if (token) {
            if (!envelope.headers) {
                envelope.headers = {};
            }
            envelope.headers["Authorization"] = this.authHeaderFormat(token);
        }
    }
    handleInboundResponse(response) {
        // Check for auth-related errors
        if (response.error) {
            const isAuthError = response.error.code === ErrorCode.UNAUTHORIZED ||
                response.error.code === ErrorCode.FORBIDDEN ||
                (response.error.code >= 4001 && response.error.code <= 4099);
            if (isAuthError) {
                this.authProvider.onTokenExpired();
            }
        }
    }
    requiresAuthentication(envelope) {
        if (!envelope.service) {
            return false;
        }
        // Check if service requires auth
        if (this.requiredAuthServices.has(envelope.service)) {
            return true;
        }
        // Check if specific method requires auth
        if (envelope.method) {
            const serviceMethods = this.requiredAuthMethods.get(envelope.service);
            if (serviceMethods && serviceMethods.has(envelope.method)) {
                return true;
            }
        }
        return false;
    }
    isRpcEnvelope(message) {
        return "kind" in message;
    }
    isRpcResponse(message) {
        return "ok" in message;
    }
}

/**
 * Circuit breaker interceptor for fault tolerance
 */
var CircuitBreakerState;
(function (CircuitBreakerState) {
    CircuitBreakerState["CLOSED"] = "closed";
    CircuitBreakerState["OPEN"] = "open";
    CircuitBreakerState["HALF_OPEN"] = "half_open";
})(CircuitBreakerState || (CircuitBreakerState = {}));
class CircuitBreaker {
    constructor(config, onStateChange) {
        this.config = config;
        this.onStateChange = onStateChange;
        this.state = CircuitBreakerState.CLOSED;
        this.failureCount = 0;
        this.successCount = 0;
        this.lastFailureTime = null;
        this.openedTime = null;
        this.recoveryTimer = null;
    }
    async execute(operation, failureCondition) {
        if (this.state === CircuitBreakerState.OPEN) {
            throw new Error("Circuit breaker is open");
        }
        try {
            const result = await operation();
            this.onSuccess();
            return result;
        }
        catch (error) {
            if (failureCondition(error)) {
                this.onFailure();
            }
            throw error;
        }
    }
    onSuccess() {
        this.failureCount = 0;
        this.successCount++;
        if (this.state === CircuitBreakerState.HALF_OPEN) {
            this.setState(CircuitBreakerState.CLOSED);
        }
    }
    onFailure() {
        this.failureCount++;
        this.lastFailureTime = Date.now();
        if (this.state === CircuitBreakerState.HALF_OPEN) {
            this.setState(CircuitBreakerState.OPEN);
        }
        else if (this.failureCount >= this.config.failureThreshold) {
            this.setState(CircuitBreakerState.OPEN);
        }
    }
    setState(newState) {
        const oldState = this.state;
        this.state = newState;
        if (newState === CircuitBreakerState.OPEN) {
            this.openedTime = Date.now();
            this.scheduleRecovery();
        }
        if (oldState !== newState && this.onStateChange) {
            this.onStateChange(newState);
        }
    }
    scheduleRecovery() {
        if (this.recoveryTimer) {
            clearTimeout(this.recoveryTimer);
        }
        this.recoveryTimer = setTimeout(() => {
            this.setState(CircuitBreakerState.HALF_OPEN);
        }, this.config.recoveryTimeout);
    }
    getState() {
        return this.state;
    }
    getMetrics() {
        return {
            state: this.state,
            failureCount: this.failureCount,
            successCount: this.successCount,
            lastFailureTime: this.lastFailureTime,
            openedTime: this.openedTime,
        };
    }
    reset() {
        this.state = CircuitBreakerState.CLOSED;
        this.failureCount = 0;
        this.successCount = 0;
        this.lastFailureTime = null;
        this.openedTime = null;
        if (this.recoveryTimer) {
            clearTimeout(this.recoveryTimer);
            this.recoveryTimer = null;
        }
    }
    destroy() {
        if (this.recoveryTimer) {
            clearTimeout(this.recoveryTimer);
        }
    }
}
class CircuitBreakerInterceptor {
    constructor(config) {
        this.name = "CircuitBreakerInterceptor";
        this.order = 30; // Run after validation but before auth
        this.defaultConfig = {
            failureThreshold: 5,
            recoveryTimeout: 30000,
            monitoringPeriod: 60000,
        };
        this.circuitBreakers = new Map();
        this.serviceConfigs = new Map();
        this.failureCondition = () => true;
        if (config) {
            this.defaultConfig = { ...this.defaultConfig, ...config };
        }
    }
    /**
     * Configure circuit breaker for a specific service
     */
    configureService(serviceName, config) {
        this.serviceConfigs.set(serviceName, { ...this.defaultConfig, ...config });
    }
    /**
     * Set custom failure condition
     */
    setFailureCondition(condition) {
        this.failureCondition = condition;
    }
    async intercept(direction, message, next) {
        // Only apply to outbound RPC calls
        if (direction !== "outbound" || !this.isRpcCall(message)) {
            return next();
        }
        const serviceName = message.service || "default";
        const circuitBreaker = this.getCircuitBreaker(serviceName);
        return circuitBreaker.execute(next, this.failureCondition);
    }
    getCircuitBreaker(serviceName) {
        if (!this.circuitBreakers.has(serviceName)) {
            const config = this.serviceConfigs.get(serviceName) || this.defaultConfig;
            const circuitBreaker = new CircuitBreaker(config);
            this.circuitBreakers.set(serviceName, circuitBreaker);
        }
        return this.circuitBreakers.get(serviceName);
    }
    isRpcCall(message) {
        return "kind" in message && message.kind === "call";
    }
    /**
     * Get current state of circuit breaker
     */
    getState(serviceName = "default") {
        const circuitBreaker = this.circuitBreakers.get(serviceName);
        return circuitBreaker
            ? circuitBreaker.getState()
            : CircuitBreakerState.CLOSED;
    }
    /**
     * Get metrics for all circuit breakers
     */
    getMetrics() {
        const metrics = {};
        // Always include default metrics
        if (!this.circuitBreakers.has("default")) {
            metrics.default = {
                state: CircuitBreakerState.CLOSED,
                failureCount: 0,
                successCount: 0,
                lastFailureTime: null,
                openedTime: null,
            };
        }
        for (const [serviceName, circuitBreaker] of this.circuitBreakers) {
            metrics[serviceName] = circuitBreaker.getMetrics();
        }
        return metrics;
    }
    /**
     * Reset all circuit breakers
     */
    reset(serviceName) {
        if (serviceName) {
            const circuitBreaker = this.circuitBreakers.get(serviceName);
            if (circuitBreaker) {
                circuitBreaker.reset();
            }
        }
        else {
            for (const circuitBreaker of this.circuitBreakers.values()) {
                circuitBreaker.reset();
            }
        }
    }
    /**
     * Cleanup resources
     */
    destroy() {
        for (const circuitBreaker of this.circuitBreakers.values()) {
            circuitBreaker.destroy();
        }
        this.circuitBreakers.clear();
    }
}

/**
 * Request queue interceptor for managing concurrency and backpressure
 */
class RequestQueue {
    constructor(config) {
        this.config = config;
        this.activeRequests = 0;
        this.queue = [];
        this.totalProcessed = 0;
        this.totalRejected = 0;
        this.totalWaitTime = 0;
        this.destroyed = false;
    }
    async process(request, next, options) {
        if (this.destroyed) {
            throw new Error("Request queue destroyed");
        }
        // If we have capacity, process immediately
        if (this.activeRequests < this.config.maxConcurrentRequests) {
            this.activeRequests++;
            return this.executeRequest(request, next);
        }
        // Check if queue is full
        if (this.queue.length >= this.config.maxQueueSize) {
            this.totalRejected++;
            throw new Error("Request queue is full");
        }
        // Queue the request
        return new Promise((resolve, reject) => {
            const queuedRequest = {
                request,
                next,
                resolve,
                reject,
                queueTime: Date.now(),
                priority: options?.priority || "normal",
            };
            // Set timeout
            queuedRequest.timeout = setTimeout(() => {
                this.removeFromQueue(queuedRequest);
                reject(new Error("Request timeout"));
            }, this.config.requestTimeout);
            // Add to queue based on priority
            if (queuedRequest.priority === "high") {
                // Add to front of queue
                this.queue.unshift(queuedRequest);
            }
            else {
                // Add to end of queue
                this.queue.push(queuedRequest);
            }
        });
    }
    async executeRequest(_request, next) {
        try {
            const result = await next();
            this.totalProcessed++;
            return result;
        }
        finally {
            this.activeRequests--;
            // Use setTimeout to ensure metrics are consistent before processing next
            setTimeout(() => this.processNext(), 0);
        }
    }
    processNext() {
        if (this.queue.length === 0 ||
            this.activeRequests >= this.config.maxConcurrentRequests) {
            return;
        }
        const queuedRequest = this.queue.shift();
        // Clear timeout
        if (queuedRequest.timeout) {
            clearTimeout(queuedRequest.timeout);
        }
        // Calculate wait time
        const waitTime = Date.now() - queuedRequest.queueTime;
        this.totalWaitTime += waitTime;
        // Increment active requests before executing
        this.activeRequests++;
        // Execute the request
        this.executeRequest(queuedRequest.request, queuedRequest.next)
            .then(queuedRequest.resolve)
            .catch(queuedRequest.reject);
    }
    removeFromQueue(queuedRequest) {
        const index = this.queue.indexOf(queuedRequest);
        if (index >= 0) {
            this.queue.splice(index, 1);
        }
    }
    getMetrics() {
        const averageWaitTime = this.totalProcessed > 0 ? this.totalWaitTime / this.totalProcessed : 0;
        return {
            activeRequests: this.activeRequests,
            queuedRequests: this.queue.length,
            totalProcessed: this.totalProcessed,
            totalRejected: this.totalRejected,
            averageWaitTime,
        };
    }
    destroy() {
        this.destroyed = true;
        // Reject all queued requests
        for (const queuedRequest of this.queue) {
            if (queuedRequest.timeout) {
                clearTimeout(queuedRequest.timeout);
            }
            // Use setTimeout to avoid synchronous rejection during teardown
            setTimeout(() => {
                queuedRequest.reject(new Error("Request queue destroyed"));
            }, 0);
        }
        this.queue.length = 0;
    }
}
class RequestQueueInterceptor {
    constructor(config) {
        this.name = "RequestQueueInterceptor";
        this.order = 40; // Run after auth but before logging
        this.defaultConfig = {
            maxConcurrentRequests: 10,
            maxQueueSize: 50,
            requestTimeout: 30000,
        };
        this.queues = new Map();
        this.serviceConfigs = new Map();
        if (config) {
            this.defaultConfig = { ...this.defaultConfig, ...config };
        }
    }
    /**
     * Configure queue for a specific service
     */
    configureService(serviceName, config) {
        this.serviceConfigs.set(serviceName, { ...this.defaultConfig, ...config });
    }
    async intercept(direction, message, next, options) {
        // Only apply to outbound RPC calls
        if (direction !== "outbound" || !this.isRpcCall(message)) {
            return next();
        }
        const serviceName = message.service || "default";
        const queue = this.getQueue(serviceName);
        return queue.process(message, next, options);
    }
    getQueue(serviceName) {
        if (!this.queues.has(serviceName)) {
            const config = this.serviceConfigs.get(serviceName) || this.defaultConfig;
            const queue = new RequestQueue(config);
            this.queues.set(serviceName, queue);
        }
        return this.queues.get(serviceName);
    }
    isRpcCall(message) {
        return "kind" in message && message.kind === "call";
    }
    /**
     * Get metrics for a specific service queue
     */
    getMetrics(serviceName = "default") {
        const queue = this.queues.get(serviceName);
        if (!queue) {
            return {
                activeRequests: 0,
                queuedRequests: 0,
                totalProcessed: 0,
                totalRejected: 0,
                averageWaitTime: 0,
            };
        }
        return queue.getMetrics();
    }
    /**
     * Get metrics for all service queues
     */
    getAllMetrics() {
        const metrics = {};
        for (const [serviceName, queue] of this.queues) {
            metrics[serviceName] = queue.getMetrics();
        }
        return metrics;
    }
    /**
     * Cleanup resources
     */
    destroy() {
        for (const queue of this.queues.values()) {
            queue.destroy();
        }
        this.queues.clear();
    }
}

/**
 * COMPAT SHIM — temporary, deleted with `src/compat`. See its README.
 *
 * ## The failure this prevents
 *
 * On affected STForestKit hosts (≤ forest-ios 5.23.0, older WebKit) the injected
 * console-bridge script replaces `console.log/debug/info/warn/error` with wrappers that
 * throw `ReferenceError` on every call: Annex B hoists the script's function
 * *declarations* out of the once-guard's `if` block but leaves its `const` bindings
 * behind, and reached through the hoisted binding those functions run with the global
 * environment, so `originalConsoleLogger` / `pendingNativeLogs` are unresolvable.
 *
 * From that moment **every `console.*` call in the document throws** — the host's own
 * scripts, this library, and the host app alike. Three consequences, all observed on a
 * physical iPhone 8 against the deployed staging site:
 *
 *   1. The RPC script dies on its first statement (a `console.log`) before defining
 *      `window.Bifrost`, so the native bridge is never created.
 *   2. This library throws from its own logging (9 call sites, and the host app enables
 *      debug logging), which surfaces as a `ReferenceError` rejecting `initialize()`.
 *   3. The host app's `catch` reports that `ReferenceError` to the user verbatim.
 *
 * (3) is why a global error listener cannot help: the throw is caught by application
 * code, so no `error` event is ever dispatched.
 *
 * ## The approach
 *
 * Install `console.log/debug/info/warn/error` as accessors at module load, before the
 * host injects. The getter always returns a guarded function; the setter records what
 * the host assigns instead of replacing the guard. The guard calls whatever the host
 * installed and, if that throws, falls back to the pristine method captured at load.
 *
 * So nothing in the document can ever throw from a `console` call, while a *healthy*
 * host's wrapper still runs and its console forwarding keeps working. That makes this
 * safe without version-gating: on a fixed host the guard is a pass-through.
 *
 * It also fixes the bridge at the source — with `console.log` no longer throwing, the
 * RPC script runs to completion on its **first** injection, so the readiness watchdog
 * never has to retry. `StForestKitOnceGuardRecovery` remains as a safety net for a host
 * whose `console` could not be guarded, or which fails for some other reason.
 */
/** Console methods the affected host replaces, and therefore the ones that can throw. */
const GUARDED_METHODS = ["log", "debug", "info", "warn", "error"];
/**
 * Pristine console methods, captured at module evaluation.
 *
 * Only as good as import ordering: the host injects on DOM-ready, so a bundle evaluated
 * before then — the normal case, and guaranteed for a dependency of the page's own
 * bundle — captures working functions. A bundle evaluated later would capture the
 * host's throwing wrappers, which is why the guard treats a throwing fallback as
 * "nothing more to try" rather than assuming the snapshot is good.
 */
const pristine = (() => {
    const snapshot = {};
    if (typeof console === "undefined") {
        return snapshot;
    }
    for (const name of GUARDED_METHODS) {
        const method = console[name];
        if (typeof method === "function") {
            snapshot[name] = method.bind(console);
        }
    }
    return snapshot;
})();
/** The pristine snapshot, for the recovery shim's own repair path. */
function pristineConsoleMethods() {
    return { ...pristine };
}
let installed = false;
/**
 * Make every `console.*` call non-throwing, permanently.
 *
 * Idempotent; a no-op when there is no `console`, and per-method tolerant of a
 * `console` whose properties cannot be redefined.
 */
function installStForestKitConsoleGuard() {
    if (installed || typeof console === "undefined") {
        return;
    }
    installed = true;
    for (const name of GUARDED_METHODS) {
        const fallback = pristine[name];
        if (!fallback) {
            continue;
        }
        // What the host most recently assigned. Starts as the pristine method so the guard
        // is a faithful pass-through until the host replaces it.
        let current = fallback;
        // Re-entrancy latch. Some code does `console.log = console.log.bind(console)`,
        // which would store a function that calls the guard again; without this the first
        // call would recurse until the stack blew. Depth is capped at one.
        let inFlight = false;
        const guarded = (...args) => {
            if (inFlight) {
                try {
                    fallback(...args);
                }
                catch {
                    /* nothing left to try */
                }
                return;
            }
            inFlight = true;
            try {
                current(...args);
            }
            catch {
                try {
                    fallback(...args);
                }
                catch {
                    /* nothing left to try */
                }
            }
            finally {
                inFlight = false;
            }
        };
        try {
            Object.defineProperty(console, name, {
                configurable: true,
                enumerable: true,
                get: () => guarded,
                set: (value) => {
                    if (typeof value === "function") {
                        current = value;
                    }
                },
            });
        }
        catch {
            // A `console` that refuses redefinition cannot be guarded. Leave it alone; the
            // recovery shim's repair path is the remaining line of defence.
        }
    }
}

/**
 * COMPAT SHIM — temporary, and designed to be deleted. See `src/compat/README.md`
 * for the removal steps and the conditions under which it becomes dead weight.
 *
 * ## What is broken
 *
 * STForestKit hosts up to and including forest-ios 5.23.0 wrap every injected plugin
 * script in a per-document once-guard that records the script's id *before* running it:
 *
 * ```js
 * if (!window.STForestKit.executedScripts.has(id)) {
 *     window.STForestKit.executedScripts.add(id);   // marked first
 *     <script>;                                     // a throw here leaves the mark
 * }
 * ```
 *
 * On WebKit the host's console-logging plugin installs wrappers that are entered
 * without the scope of the block that declared their dependencies, so calling any
 * `console` method throws `ReferenceError`. The `BifrostRPC` plugin script's first
 * statement inside its guard is a `console.log`, so it dies there — before defining
 * either `window.Bifrost` or `window.bifrostNativeBridge` — with its id already
 * marked. The host's readiness watchdog then re-injects every 500ms and every
 * attempt short-circuits on the guard, so the bridge stays dead for the lifetime of
 * the document and this library's handshake can never complete.
 *
 * Fixed host-side in stforestkit#446 (marks after the script runs, and the console
 * wrappers no longer depend on call-time lexical state). That fix cannot be applied
 * retroactively to installed binaries, which is why this shim exists.
 *
 * ## What this does
 *
 * Both halves of the broken state live on this page's own `window` — the host
 * evaluates its scripts in the main world — so the page can repair it:
 *
 *   1. put a callable `console` back, so the RPC script's first statement survives;
 *   2. clear *only* the `BifrostRPC` mark, and let the host's own watchdog re-inject.
 *
 * We deliberately do not inject anything ourselves. The host's watchdog is already
 * running for exactly as long as the page is un-ready, and its injected script is
 * idempotent by construction (`if (!window.Bifrost)` / `if (window.Bifrost &&
 * !window.bifrostNativeBridge)`), so unblocking it is both sufficient and the
 * smallest possible intervention.
 *
 * ## Why this cannot fire on a healthy host
 *
 * The precondition is `mark present AND bridge absent`, which is reachable only on an
 * affected host. A fixed host records the mark only after the script has run, so if
 * the mark is there the bridge is there too and we return before touching anything.
 * A host that has not injected yet has no mark. A non-STForestKit host has no
 * `window.STForestKit`. In all three cases this is a no-op.
 */
/** The host's plugin id for the script that installs the native bridge. */
const RPC_PLUGIN_ID = "BifrostRPC";
/**
 * Console methods the host's logging bridge replaces, and therefore the ones that
 * can be left throwing.
 */
const WRAPPED_METHODS = ["log", "debug", "info", "warn", "error"];
/**
 * Minimum spacing between attempts. The host watchdog re-injects on a 500ms tick, so
 * anything tighter would clear the mark again before the re-injection it is waiting
 * for has had a chance to run.
 */
const MIN_ATTEMPT_INTERVAL_MS = 750;
/**
 * Attempts allowed per document. A handful covers a re-injection that lands late or
 * throws once more; beyond that the failure is not the one this shim describes, and
 * repeatedly clearing the mark would keep the host re-running a script that has no
 * prospect of succeeding.
 */
const MAX_ATTEMPTS = 4;
const pristineConsole = pristineConsoleMethods();
// Installed at module load, before the host can replace `console`. This is the primary
// fix: with console calls unable to throw, the host's RPC script survives its opening
// `console.log` and installs the bridge on its first injection, and neither this library
// nor the host app can be thrown at from their own logging. Everything below is the
// remaining safety net for a host whose console could not be guarded.
installStForestKitConsoleGuard();
let attempts = 0;
let lastAttemptAt = 0;
/**
 * Whether calling `console.log` currently throws.
 *
 * Invoking it is the only way to find out — a poisoned wrapper is an ordinary callable
 * function and is indistinguishable by inspection. Reached only once the stuck
 * precondition already holds, so a healthy page never emits this.
 */
function consoleThrows() {
    try {
        // eslint-disable-next-line no-console -- probing callability is the point
        console.log();
        return false;
    }
    catch {
        return true;
    }
}
function assignConsole(methods) {
    for (const name of WRAPPED_METHODS) {
        const replacement = methods[name];
        if (replacement) {
            try {
                console[name] = replacement;
            }
            catch {
                // A host that made these non-writable is not one we can repair; the caller
                // still clears the mark, which is harmless, and the handshake still fails
                // on its own deadline rather than here.
            }
        }
    }
}
/**
 * Leave `console` callable.
 *
 * Restores the snapshot when it works, and neutralises to no-ops when it does not —
 * losing devtools output is a far smaller cost than leaving the RPC script unable to
 * execute its first statement.
 */
function repairConsole() {
    if (!consoleThrows()) {
        return "healthy";
    }
    assignConsole(pristineConsole);
    if (!consoleThrows()) {
        return "restored";
    }
    const noop = () => {
        /* nothing — better than throwing */
    };
    assignConsole({
        log: noop,
        debug: noop,
        info: noop,
        warn: noop,
        error: noop,
    });
    return "neutralised";
}
/**
 * Clear the host's stale `BifrostRPC` mark so its readiness watchdog can retry.
 *
 * Only these two ids are ever touched, and only `BifrostRPC` is cleared:
 *
 * - `ConsoleLoggingBridge` is left marked on purpose. Re-running it recreates the
 *   same broken lexical dependency and wraps an already-wrapped console. The cost is
 *   losing the host's console forwarding, which is host-side logging only.
 * - `MessageEventBridge` is left marked on purpose. On affected hosts its script does
 *   not remove a previously registered listener, so a re-injection stacks one and
 *   doubles every web→native message — duplicate analytics, duplicate URL opens.
 *   stforestkit#446's third change exists for precisely this reason.
 *
 * @returns true when a stale mark was cleared and a re-injection is now expected.
 */
function attemptStForestKitOnceGuardRecovery(now = Date.now()) {
    if (typeof window === "undefined") {
        return false;
    }
    // The bridge is present, so whatever happened the host got there. Nothing to fix,
    // and this is the branch that keeps the shim off every fixed host.
    if (window.bifrostNativeBridge) {
        return false;
    }
    const marks = window.STForestKit?.executedScripts;
    if (!marks || typeof marks.delete !== "function") {
        return false; // not an STForestKit host, or the guard is not set up yet
    }
    // No mark means the host has not run the script yet — it is not stuck, it is early.
    if (!marks.has(RPC_PLUGIN_ID)) {
        return false;
    }
    if (attempts >= MAX_ATTEMPTS ||
        now - lastAttemptAt < MIN_ATTEMPT_INTERVAL_MS) {
        return false;
    }
    attempts += 1;
    lastAttemptAt = now;
    repairConsole();
    try {
        marks.delete(RPC_PLUGIN_ID);
    }
    catch {
        return false;
    }
    return true;
}

/**
 * NativeHandshake - owns the native-bridge handshake end to end.
 *
 * Split out of `BifrostBridge` because the handshake's invariants are about ordering,
 * lifetime and re-entrancy, and they only hold if they are enforced in one place.
 * Interleaved with RPC routing they had no locality: a caller could reach the state
 * that host code was concurrently mutating, and two owners scheduled evaluation.
 *
 * Everything about how a handshake progresses is hidden here -- which shim a send and
 * an ack belong to, how host re-entrancy is serialized, how the deadline and the
 * shared-watch memo interact, and when evaluation is scheduled. The RPC side only
 * awaits an outcome, forwards `native_ready`, and stops it on teardown.
 */
class NativeHandshake {
    /** Monotonic so ids never collide within a document; not security-relevant. */
    static nextHandshakeId() {
        NativeHandshake.handshakeCounter += 1;
        return `hs-${NativeHandshake.handshakeCounter}`;
    }
    constructor(host) {
        this.host = host;
    }
    /**
     * Resolve when the handshake completes, reject at `timeoutMs`.
     *
     * Overlapping callers share one attempt, so the shim is sent one `onWebReady()`
     * between them rather than one each.
     */
    async await(timeoutMs = DEFAULT_READY_TIMEOUT_MS) {
        const watch = this.bridgeWatch(timeoutMs);
        let timeoutTimer;
        const timeoutPromise = new Promise((_, reject) => {
            timeoutTimer = setTimeout(() => reject(new Error("Native bridge ready timeout")), timeoutMs);
        });
        try {
            // No `watch.stop()` on success: `bridgeWatch` registers its own retirement on
            // `settled` first, so it has always run by the time this resumes.
            await Promise.race([watch.settled, timeoutPromise]);
        }
        finally {
            clearTimeout(timeoutTimer);
        }
    }
    /** Route an inbound `native_ready` to the attempt in flight, if any. */
    handleNativeReady(message) {
        this.pendingWatch?.signalNativeReady(typeof message === "string"
            ? { bare: true }
            : { bare: false, handshakeId: message.handshakeId });
    }
    /**
     * Either the legacy bare string, or an envelope echoing back the id we passed to
     * `onWebReady()`. The echoing form is what lets an asynchronous ack be attributed
     * to the send it answers; a bare one only counts synchronously.
     */
    isNativeReady(message) {
        return (message === "native_ready" ||
            (typeof message === "object" &&
                message !== null &&
                message.type === "native_ready"));
    }
    /** Abandon any attempt in flight. */
    stop() {
        this.pendingWatch?.stop();
    }
    /**
     * Advance `watch` against whichever shim is on `window` right now, re-arming from
     * scratch if that is not the one the attempt in progress was bound to.
     *
     * Returns true once the bound shim has completed both halves of the handshake.
     */
    advanceHandshake(watch) {
        const shim = typeof window === "undefined" ? undefined : window.bifrostNativeBridge;
        if (shim !== watch.generation) {
            watch.generation = shim;
            watch.sent = false;
            watch.acked = false;
            // A fresh send needs a fresh id, so acks still owed to the previous shim
            // cannot answer it.
            watch.handshakeId = NativeHandshake.nextHandshakeId();
        }
        if (!shim) {
            // BEGIN stforestkit#469 compat — delete this block with `src/compat` (see its README).
            // The shim is missing, which on an affected host may mean its injection was
            // permanently skipped by a stale once-guard mark. Self-limiting: a no-op unless
            // that exact state is present.
            attemptStForestKitOnceGuardRecovery();
            // END stforestkit#469 compat
            return false;
        }
        if (!watch.sent) {
            if (typeof shim.onWebReady !== "function") {
                return false;
            }
            // Marked before the call, not after: the injected shim acks synchronously
            // from inside `onWebReady()`, so `signalNativeReady` re-enters here while the
            // call is still on the stack and would otherwise reject its own ack.
            watch.sent = true;
            watch.sending = true;
            try {
                // Hosts that echo the id back can be attributed exactly; the rest are
                // covered by the synchronous window this flag opens.
                shim.onWebReady(watch.handshakeId);
            }
            catch (error) {
                // A throw leaves the send unanswered, so roll the mark back and let the
                // next tick try the same shim again — otherwise a momentary host fault is
                // permanent unless the host happens to swap the shim out. An ack that
                // already landed inside the call still answers it, and survives.
                if (!watch.acked) {
                    watch.sent = false;
                    watch.handshakeId = NativeHandshake.nextHandshakeId();
                    throw error;
                }
            }
            finally {
                watch.sending = false;
            }
        }
        const ready = watch.acked || Boolean(shim.isReady);
        // Both `onWebReady()` and the `isReady` getter are host code and either can swap
        // the shim mid-pass. One check after both have run covers either seam: readiness
        // reported by an outgoing generation must not settle the incoming one, which has
        // been sent nothing and has reset neither half of the handshake.
        if (window.bifrostNativeBridge !== shim) {
            return false;
        }
        return ready;
    }
    /**
     * Poll the handshake until it completes, this bridge is destroyed, or the watch
     * reaches its deadline.
     *
     * The poll has to keep going for the caller's whole window, not just until the
     * shim first appears: the host injects it on DOM-ready and re-injects on a
     * watchdog, so on a slow page it can land seconds in. A deadline shorter than the
     * host's own leaves the page unable to hand shake even though the bridge is
     * present on `window` — indistinguishable downstream from a healthy bridge that
     * never delivered anything. See `DEFAULT_READY_TIMEOUT_MS`.
     */
    watchForNativeBridge(watch, settle) {
        // Nothing more should be attempted once any of these holds. Read as a function
        // rather than captured, because `advanceHandshake` runs host code that can flip
        // all three from underneath us.
        const terminal = () => this.host.isDestroyed() || watch.stopped || Date.now() > watch.deadline;
        // Exactly one future evaluation is ever queued, whether it was asked for by the
        // regular poll, by a drain that ran out of passes, or by an ack. Letting each
        // source keep its own timer meant a yielded drain and the poll both scheduled
        // successors, so every poll interval added another evaluation stream.
        let handle;
        let scheduledFor = Infinity;
        const schedule = (delayMs) => {
            const runAt = Date.now() + delayMs;
            if (handle !== undefined) {
                // Keeping the earliest is what stops a stream of poll-length reschedules
                // from pushing a yielded drain out indefinitely. Clearing before setting is
                // the part that keeps the count at one; no test separates the two, since
                // dropping this only lengthens a delay rather than adding a timer.
                if (runAt >= scheduledFor) {
                    return;
                }
                clearTimeout(handle);
            }
            scheduledFor = runAt;
            handle = setTimeout(() => {
                handle = undefined;
                scheduledFor = Infinity;
                run();
            }, delayMs);
        };
        /** One pass over the handshake, draining reruns requested from host code. */
        const evaluate = () => {
            if (watch.evaluating) {
                // Re-entered from host code. Ask for another pass instead of nesting: the
                // loop below drains the request once the current pass unwinds, so depth
                // stays at one however long a chain of self-replacing shims gets.
                watch.rerunRequested = true;
                return "pending";
            }
            watch.evaluating = true;
            try {
                for (let pass = 0; pass < NativeHandshake.MAX_EVALUATION_PASSES; pass++) {
                    watch.rerunRequested = false;
                    if (terminal()) {
                        return "pending";
                    }
                    let ready = false;
                    try {
                        ready = this.advanceHandshake(watch);
                    }
                    catch {
                        // `onWebReady` and the `isReady` getter are host code. A throwing shim
                        // is just a not-ready one; letting it escape would kill the poll loop
                        // while leaving the watch un-stopped in the shared memo, so every later
                        // caller inherits a handshake that can never complete.
                        ready = false;
                    }
                    // Re-checked after the fact: that same host code can destroy the bridge
                    // or stop this watch from inside the call, and a handshake completed
                    // against a bridge that no longer exists must not resolve anybody's
                    // `initialize()`.
                    if (terminal()) {
                        return "pending";
                    }
                    if (ready) {
                        settle();
                        return "ready";
                    }
                    if (!watch.rerunRequested) {
                        return "pending";
                    }
                }
                // The host is asking for passes without ever completing. Yield rather than
                // spinning here -- draining inline forever would block the page just as
                // surely as the recursion this loop replaced -- and let the caller queue the
                // continuation, so it does not become a second source of timers.
                return "yielded";
            }
            finally {
                watch.evaluating = false;
            }
        };
        const run = () => {
            const outcome = evaluate();
            if (outcome === "ready" || terminal()) {
                return;
            }
            schedule(outcome === "yielded" ? 0 : NativeHandshake.BRIDGE_POLL_INTERVAL_MS);
        };
        // An ack evaluates now and reschedules through the same single handle, so it
        // brings the next evaluation earlier rather than adding one.
        watch.evaluate = run;
        run();
    }
    /**
     * The in-flight handshake, shared by every `initialize()` call that overlaps it,
     * so overlapping callers send one `onWebReady()` between them rather than one
     * each when the shim lands.
     *
     * A watch is reused only while it is still running and its deadline still covers
     * the new caller's whole timeout window — otherwise the caller could outlive the
     * watcher it is relying on and miss a shim that arrives inside its own timeout.
     * `stop()` on handshake completion is what drops the memo, so polling never
     * continues past a successful handshake.
     */
    bridgeWatch(timeoutMs) {
        const now = Date.now();
        const required = now + timeoutMs;
        const existing = this.pendingWatch;
        // A live watch is extended, never replaced. Replacing it would strand any
        // caller already awaiting its promises on promises that can now never settle,
        // so that caller would reject at its own timeout even if the replacement
        // completed the handshake first. Extending also keeps the deadline covering
        // every live caller's window, which is what stops a caller from outliving the
        // watch it depends on.
        if (existing && !existing.stopped && now <= existing.deadline) {
            existing.deadline = Math.max(existing.deadline, required);
            return existing;
        }
        existing?.stop();
        // `settled` is a real deferred from the outset, never a placeholder swapped out
        // afterwards: the first poll tick runs synchronously below, and host code
        // reached from it can re-enter `initialize()` and be handed this same watch out
        // of the memo. A half-built watch carrying an already-resolved stand-in would
        // report a completed handshake to that caller.
        let settle;
        const settled = new Promise((resolve) => {
            settle = resolve;
        });
        const watch = {
            deadline: required,
            stopped: false,
            sent: false,
            acked: false,
            sending: false,
            evaluating: false,
            rerunRequested: false,
            handshakeId: NativeHandshake.nextHandshakeId(),
            settled,
            stop: () => {
                watch.stopped = true;
                if (this.pendingWatch === watch) {
                    this.pendingWatch = undefined;
                }
            },
            // An ack has to answer *this watch's current send*. Two ways to establish
            // that:
            //
            //  1. The host echoed back the id we passed to `onWebReady(handshakeId)`.
            //  2. The host sent the legacy *bare* `"native_ready"` while that call was
            //     still on the stack, so no other send could have produced it.
            //
            // The synchronous window is for the bare form only. An envelope carries an id
            // and is therefore always attributable, so one naming a different send is a
            // straggler however it arrives -- being delivered synchronously does not make
            // it answer the call it was not addressed to.
            //
            // An asynchronous bare ack is refused for the same reason: it cannot be told
            // apart from a replaced shim's straggler. Such hosts can still complete
            // through `shim.isReady`.
            signalNativeReady: (ack) => {
                // No separate "have we sent yet" check: a bare ack is refused unless the send
                // is on the stack, and an envelope can only echo an id the host was given by
                // a send, so neither form can be credited before one.
                const answersThisSend = ack.bare
                    ? watch.sending
                    : ack.handshakeId === watch.handshakeId;
                if (!answersThisSend) {
                    return;
                }
                watch.acked = true;
                // A tick may be up to a poll interval away and the caller's deadline can
                // fall inside that gap, so a valid ack is acted on now rather than merely
                // recorded. Safe to call unconditionally: `evaluate` serializes, so an ack
                // raised from inside host code queues a further pass instead of recursing.
                watch.evaluate?.();
            },
        };
        // Retiring the memo cannot depend on a caller observing the success: the
        // `initialize()` that owned the await may have already rejected on its own
        // timeout, leaving the watch to complete with nobody watching. A fulfilled
        // watch must not then be handed to a later caller as if it were current.
        void settled.then(() => {
            watch.stop();
            this.host.onCompleted();
        });
        // Published before polling starts, not after: the first tick runs synchronously
        // and the injected shim acks synchronously from inside `onWebReady()`, so
        // `handleGlobalMessage` re-enters while this is still being built. Assigning
        // afterwards would leave `pendingWatch` unset for exactly that ack.
        this.pendingWatch = watch;
        this.watchForNativeBridge(watch, settle);
        return watch;
    }
}
NativeHandshake.handshakeCounter = 0;
/**
 * Upper bound on passes drained in one synchronous evaluation. Reached only by a
 * host that keeps asking for another pass without ever completing the handshake.
 */
NativeHandshake.MAX_EVALUATION_PASSES = 64;
/** Interval between native-bridge presence checks. */
NativeHandshake.BRIDGE_POLL_INTERVAL_MS = 100;

/**
 * Per-chunk allowance added to a chunked call's deadline.
 *
 * Each chunk costs a bridge crossing plus a JSON parse on both ends, and the
 * sender yields to the event loop periodically, so the cost of a large message
 * is roughly linear in chunk count. 60ms is deliberately generous against the
 * ~12.5ms a chunk actually needs — the deadline exists to catch a hung bridge,
 * not to police throughput, and a false timeout on a healthy large transfer is
 * far more damaging than a slow one.
 */
const PER_CHUNK_ALLOWANCE_MS = 60;
function chunkTransferAllowanceMs(chunkCount) {
    return Math.max(0, chunkCount) * PER_CHUNK_ALLOWANCE_MS;
}
/**
 * Deadline for an outbound call, in milliseconds.
 *
 * An unchunked message keeps the caller's base deadline. A chunked one gets an
 * allowance per chunk on top: at the base alone the advertised maxTransferSize
 * is unreachable by construction, so a caller sending a large payload would
 * always see a timeout regardless of how healthy the transport is.
 *
 * Callers passing an explicit timeout are not overridden — this only supplies
 * the default.
 */
function callDeadlineMs(serializedBytes, chunkThreshold, chunkSize, baseMs) {
    if (serializedBytes <= chunkThreshold || chunkSize <= 0) {
        return baseMs;
    }
    const chunks = maxUtf8ChunksForByteLength(serializedBytes, chunkSize);
    return baseMs + chunkTransferAllowanceMs(chunks);
}

let fallbackId = 0;
function freshId() {
    const crypto = globalThis.crypto;
    if (crypto?.getRandomValues) {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    }
    fallbackId += 1;
    return `document-${Date.now()}-${fallbackId}`;
}
class V2Session {
    constructor(adapter, onMessage, onFault = () => undefined, onChunkAccepted = () => undefined) {
        this.adapter = adapter;
        this.onMessage = onMessage;
        this.onFault = onFault;
        this.onChunkAccepted = onChunkAccepted;
        this.state = "idle";
    }
    initialize(timeoutMs) {
        if (this.ready)
            return this.ready;
        this.ready = this.negotiate(timeoutMs);
        return this.ready;
    }
    isReady() {
        return this.state === "ready";
    }
    directLimit() {
        return this.grant?.limits.maxDirectFrameBytes ?? 0;
    }
    multipartChunkSize() {
        return this.grant?.limits.multipartChunkBytes ?? 128 * 1024;
    }
    admissionWindow() {
        return this.grant?.limits.admissionWindow ?? 0;
    }
    send(message) {
        if (!this.grant || this.state !== "ready")
            throw new Error("V2 session is not ready");
        const kind = "kind" in message ? message.kind : "response";
        const serialized = JSON.stringify(message);
        const sizeBytes = utf8ByteLength(serialized);
        if (sizeBytes > this.directLimit()) {
            const limits = this.grant.limits;
            if (!this.grant.features.includes("multipart") ||
                !limits.maxMultipartBytes ||
                !limits.multipartChunkBytes ||
                sizeBytes > limits.maxMultipartBytes)
                throw new PayloadTooLargeError();
            const transferId = freshId();
            for (const chunk of splitIntoChunks(transferId, serialized, limits.multipartChunkBytes, "id" in message ? message.id : undefined)) {
                if (this.adapter.send(JSON.stringify({ ...this.binding("multipart_chunk"), chunk })) === "not_admitted")
                    return "not_admitted";
            }
            return "admitted";
        }
        return (this.adapter.send(JSON.stringify({ ...this.binding(kind), ...message, kind })) ?? "admitted");
    }
    close() {
        if (this.state === "closed")
            return;
        this.state = "closed";
        this.resolveReady?.(false);
        this.detachReceiver?.();
        this.detachReceiver = undefined;
        this.grant = undefined;
        this.multipart?.destroy();
    }
    recover(timeoutMs) {
        if (this.state !== "faulted")
            return this.initialize(timeoutMs);
        this.ready = undefined;
        this.resolveReady = undefined;
        this.grant = undefined;
        this.multipart?.destroy();
        this.multipart = undefined;
        this.state = "idle";
        return this.initialize(timeoutMs);
    }
    async negotiate(timeoutMs) {
        const hello = {
            kind: "hello",
            documentNonce: freshId(),
            handshakeAttempt: freshId(),
            protocols: [2],
            features: ["multipart"],
            webToNativeTransports: ["ios_wk_reply", "android_js_interface"],
            nativeToWebTransports: ["ios_reply_rearm", "android_web_message_port"],
            representations: ["base64"],
            limits: {
                maxDirectFrameBytes: 262144,
                admissionWindow: 8,
                controlPriorityWindow: 2,
                maxMultipartBytes: 16 * 1024 * 1024,
                multipartChunkBytes: 64 * 1024,
                maxActiveBulkTransfers: 2,
            },
        };
        const outcome = await this.adapter.hello(hello);
        if (outcome.kind === "session_rejected" || !this.validGrant(outcome))
            return false;
        this.grant = outcome;
        if (outcome.features.includes("multipart")) {
            this.multipart = new ChunkAssembler({
                maxConcurrentTransfers: outcome.limits.maxActiveBulkTransfers,
                maxTransferBytes: outcome.limits.maxMultipartBytes,
                maxTotalBytes: outcome.limits.maxMultipartBytes *
                    outcome.limits.maxActiveBulkTransfers,
                onAccept: (chunk) => this.onChunkAccepted(chunk),
            });
        }
        this.state = "arming";
        this.detachReceiver = this.adapter.arm((frame) => this.receive(frame));
        const readyAck = new Promise((resolve) => {
            this.resolveReady = resolve;
        });
        const timeout = setTimeout(() => this.resolveReady?.(false), timeoutMs);
        if (this.adapter.send(JSON.stringify(this.binding("session_ready"))) ===
            "not_admitted") {
            this.close();
            return false;
        }
        const ready = await readyAck;
        clearTimeout(timeout);
        if (!ready)
            this.close();
        return ready;
    }
    receive(frameText) {
        if (!this.grant || this.state === "closed" || this.state === "faulted")
            return;
        let frame;
        try {
            const parsed = JSON.parse(frameText);
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
                return;
            frame = parsed;
        }
        catch {
            return;
        }
        if (frame.protocol !== 2 ||
            frame.sessionId !== this.grant.sessionId ||
            frame.capability !== this.grant.capability)
            return;
        if (frame.kind === "session_ready_ack" && this.state === "arming") {
            this.state = "ready";
            this.resolveReady?.(true);
            return;
        }
        if (this.state !== "ready")
            return;
        if (frame.kind === "multipart_chunk") {
            const responseId = frame.chunk?.responseId;
            const assembled = frame.chunk && this.multipart?.addChunk(frame.chunk);
            if (!assembled)
                return;
            let application;
            try {
                application = JSON.parse(assembled);
            }
            catch {
                return this.fault();
            }
            if (responseId !== undefined && application.id !== responseId) {
                return this.fault();
            }
            this.receiveApplication(application);
        }
        else if (frame.kind === "call" || frame.kind === "notify") {
            if (!frame.id || !frame.service || !frame.method)
                return this.fault();
            this.onMessage({
                id: frame.id,
                kind: frame.kind,
                service: frame.service,
                method: frame.method,
                payload: frame.payload,
                ts: typeof frame.ts === "number" ? frame.ts : Date.now(),
            });
        }
        else if (frame.kind === "response") {
            if (!frame.id || typeof frame.ok !== "boolean")
                return this.fault();
            this.onMessage({
                id: frame.id,
                ok: frame.ok,
                result: frame.result,
                error: frame.error,
                ts: typeof frame.ts === "number" ? frame.ts : Date.now(),
            });
        }
        else {
            this.fault();
        }
    }
    receiveApplication(message) {
        if ("kind" in message) {
            if ((message.kind !== "call" && message.kind !== "notify") ||
                !message.id ||
                !message.service ||
                !message.method)
                return this.fault();
        }
        else if (!message.id || typeof message.ok !== "boolean") {
            return this.fault();
        }
        this.onMessage(message);
    }
    binding(kind) {
        return {
            protocol: 2,
            sessionId: this.grant.sessionId,
            capability: this.grant.capability,
            kind,
        };
    }
    validGrant(grant) {
        return (grant.protocol === 2 &&
            grant.sessionId.length > 0 &&
            grant.capability.length >= 22 &&
            Array.isArray(grant.features) &&
            ["ios_wk_reply", "android_js_interface"].includes(grant.webToNativeTransport) &&
            ["ios_reply_rearm", "android_web_message_port"].includes(grant.nativeToWebTransport) &&
            Number.isSafeInteger(grant.limits.maxDirectFrameBytes) &&
            grant.limits.maxDirectFrameBytes > 0 &&
            Number.isSafeInteger(grant.limits.admissionWindow) &&
            grant.limits.admissionWindow > 0 &&
            Number.isSafeInteger(grant.limits.controlPriorityWindow) &&
            grant.limits.controlPriorityWindow > 0 &&
            (!grant.features.includes("multipart") ||
                (Number.isSafeInteger(grant.limits.maxMultipartBytes) &&
                    grant.limits.maxMultipartBytes > grant.limits.maxDirectFrameBytes &&
                    Number.isSafeInteger(grant.limits.multipartChunkBytes) &&
                    grant.limits.multipartChunkBytes > 0 &&
                    Number.isSafeInteger(grant.limits.maxActiveBulkTransfers) &&
                    grant.limits.maxActiveBulkTransfers > 0)));
    }
    fault() {
        if (this.state === "faulted" || this.state === "closed")
            return;
        this.state = "faulted";
        this.detachReceiver?.();
        this.detachReceiver = undefined;
        this.onFault();
    }
}

/**
 * BifrostBridge - Singleton that manages native bridge communication
 *
 * This class handles the actual WebView bridge connection and coordinates
 * message passing between multiple BifrostWeb client instances.
 */
const DEFAULT_CALL_TIMEOUT_MS = 5000;
function advertisedDirectLimit(nativeBridge = typeof window === "undefined"
    ? undefined
    : window.bifrostNativeBridge) {
    const limit = nativeBridge?.maxUnchunkedMessageBytes;
    return typeof limit === "number" && Number.isSafeInteger(limit) && limit > 0
        ? limit
        : undefined;
}
/**
 * Singleton bridge that manages communication with the native side
 */
class BifrostBridge {
    constructor() {
        this.clients = new Map();
        this.pendingCalls = new Map();
        this.handlerInitialized = false;
        this.isDestroyed = false;
        this.droppedV2Notifications = 0;
        this.admittedCalls = 0;
        this.admissionWaiters = [];
        /**
         * Owns the handshake outright: which shim a send and an ack belong to, host
         * re-entrancy, the deadline, the shared attempt, and when evaluation is scheduled.
         * This class only awaits an outcome, forwards `native_ready`, and stops it.
         */
        this.handshake = new NativeHandshake({
            isDestroyed: () => this.isDestroyed,
            onCompleted: () => this.markClientsReady(),
        });
        this.chunkAssembler = new ChunkAssembler({
            onAccept: (chunk) => this.extendResponseDeadline(chunk),
            onReject: (transferId, reason) => {
                // An inbound transfer that dies must say so. Without this the message
                // simply never arrives and the caller can only infer it from a timeout.
                // eslint-disable-next-line no-console
                console.error(`[BifrostBridge] Inbound multipart transfer ${transferId.length > 128 ? `${transferId.slice(0, 128)}…` : transferId} rejected: ${reason}`);
            },
        });
        this.chunkSender = null;
        this.chunkConfig = {};
        this.setupGlobalHandler();
    }
    /**
     * Get the singleton instance
     */
    static getInstance() {
        if (!BifrostBridge.instance || BifrostBridge.instance.isDestroyed) {
            BifrostBridge.instance = new BifrostBridge();
        }
        return BifrostBridge.instance;
    }
    /**
     * Initialize the bridge connection
     */
    async initialize(readyTimeout = DEFAULT_READY_TIMEOUT_MS) {
        if (this.isDestroyed) {
            throw new Error("BifrostBridge has been destroyed");
        }
        const v2Adapter = typeof window === "undefined" ? undefined : window.bifrostV2Bootstrap;
        if (v2Adapter) {
            this.v2Session ?? (this.v2Session = new V2Session(v2Adapter, (message) => void this.handleGlobalMessage(message), () => this.handleV2Fault(), (chunk) => this.extendResponseDeadline(chunk)));
            if (await this.v2Session.initialize(readyTimeout)) {
                this.markClientsReady();
                return;
            }
            this.v2Session.close();
            this.v2Session = undefined;
        }
        await this.handshake.await(readyTimeout);
    }
    /**
     * Register a client with the bridge
     */
    registerClient(client) {
        if (this.isDestroyed) {
            throw new Error("BifrostBridge has been destroyed");
        }
        this.clients.set(client.clientId, client);
    }
    /**
     * Unregister a client from the bridge
     */
    unregisterClient(clientId) {
        // Remove client
        this.clients.delete(clientId);
        // Cancel any pending calls for this client
        for (const [callId, pendingCall] of this.pendingCalls) {
            if (pendingCall.clientId === clientId) {
                if (pendingCall.timeout)
                    clearTimeout(pendingCall.timeout);
                pendingCall.reject(new Error("Client instance destroyed"));
                this.pendingCalls.delete(callId);
            }
        }
        // If no more clients, cleanup global handler
        if (this.clients.size === 0) {
            this.cleanup();
        }
    }
    /**
     * Send an RPC call through the bridge
     */
    async sendCall(clientId, callId, service, method, payload, options, envelope, chunkConfig = this.chunkConfig) {
        if (this.isDestroyed) {
            throw new Error("BifrostBridge has been destroyed");
        }
        // Use provided envelope or create a new one
        const finalEnvelope = envelope || {
            id: callId,
            kind: "call",
            service,
            method,
            payload,
            ts: Date.now(),
        };
        const sizeBytes = this.prepareCallMessage(finalEnvelope, options, chunkConfig);
        const chunkSize = this.v2Session?.isReady()
            ? this.v2Session.multipartChunkSize()
            : (chunkConfig.chunkSize ?? 128 * 1024);
        return new Promise((resolve, reject) => {
            // A chunked message needs longer than the base deadline; at 5s the
            // advertised maxTransferSize is unreachable by construction, so a large
            // payload would always time out however healthy the transport is. An
            // explicit caller timeout still wins.
            this.pendingCalls.set(callId, {
                resolve,
                reject,
                clientId,
                deadlineAt: 0,
                canExtendResponseDeadline: options?.timeout === undefined,
            });
            this.sendToNative(finalEnvelope, undefined, (chunkThreshold) => {
                const pending = this.pendingCalls.get(callId);
                if (!pending)
                    return;
                const baseDeadline = options?.timeout ??
                    (finalEnvelope.deadlineAt === undefined
                        ? DEFAULT_CALL_TIMEOUT_MS
                        : Math.max(0, finalEnvelope.deadlineAt - Date.now()));
                const deadline = options?.timeout ??
                    (finalEnvelope.deadlineAt === undefined
                        ? callDeadlineMs(sizeBytes, chunkThreshold, chunkSize, baseDeadline)
                        : baseDeadline);
                const pendingDeadlineAt = finalEnvelope.deadlineAt ?? Date.now() + deadline;
                pending.deadlineAt = pendingDeadlineAt;
                pending.timeout = setTimeout(() => {
                    this.pendingCalls.delete(callId);
                    reject(this.v2Session?.isReady()
                        ? new DeliveryUnknownError()
                        : new Error("RPC call timeout"));
                }, Math.max(0, pendingDeadlineAt - Date.now()));
            }, () => this.pendingCalls.has(callId), callId, chunkConfig);
        });
    }
    prepareCallMessage(envelope, options, chunkConfig) {
        const originalDeadlineAt = envelope.deadlineAt;
        let sizeBytes = prepareMessage(envelope);
        if (options?.timeout === undefined && originalDeadlineAt !== undefined) {
            const now = Date.now();
            const baseMs = Math.max(0, originalDeadlineAt - now);
            const { chunkThreshold, chunkSize } = this.callTransportLimits(chunkConfig);
            for (let attempt = 0; attempt < 3; attempt++) {
                const allowance = callDeadlineMs(sizeBytes, chunkThreshold, chunkSize, baseMs) - baseMs;
                const adjustedDeadlineAt = originalDeadlineAt + allowance;
                if (envelope.deadlineAt === adjustedDeadlineAt)
                    break;
                envelope.deadlineAt = adjustedDeadlineAt;
                sizeBytes = prepareMessage(envelope);
            }
        }
        return sizeBytes;
    }
    callTransportLimits(chunkConfig) {
        if (this.v2Session?.isReady()) {
            return {
                chunkThreshold: this.v2Session.directLimit(),
                chunkSize: this.v2Session.multipartChunkSize(),
            };
        }
        const nativeBridge = typeof window === "undefined" ? undefined : window.bifrostNativeBridge;
        return {
            chunkThreshold: chunkConfig.chunkThreshold ??
                advertisedDirectLimit(nativeBridge) ??
                256 * 1024,
            chunkSize: chunkConfig.chunkSize ?? 128 * 1024,
        };
    }
    admitCall(deadlineAt, localLimit, signal) {
        if (signal?.aborted)
            return Promise.reject(new CallCancelledError());
        const capacity = Math.min(localLimit, this.v2Session?.admissionWindow() || localLimit);
        if (this.admittedCalls < capacity && this.admissionWaiters.length === 0) {
            this.admittedCalls++;
            return Promise.resolve(this.releaseAdmission());
        }
        if (this.admissionWaiters.length >= localLimit)
            return Promise.reject(new OverloadedError());
        return new Promise((resolve, reject) => {
            const waiter = {
                deadlineAt,
                localLimit,
                resolve,
                reject,
                timer: setTimeout(() => {
                    this.removeWaiter(waiter);
                    reject(new CallDeadlineExceededError());
                }, Math.max(0, deadlineAt - Date.now())),
                signal,
            };
            waiter.abort = () => {
                this.removeWaiter(waiter);
                reject(new CallCancelledError());
            };
            signal?.addEventListener("abort", waiter.abort, { once: true });
            this.admissionWaiters.push(waiter);
        });
    }
    async recoverV2(deadlineAt) {
        if (!this.v2Session || this.v2Session.isReady())
            return;
        const recovered = await this.v2Session.recover(Math.max(0, deadlineAt - Date.now()));
        if (!recovered)
            throw new SessionReplacedError();
    }
    /**
     * Send a notification through the bridge
     */
    sendNotification(service, method, payload, callId, chunkConfig = this.chunkConfig) {
        if (this.isDestroyed) {
            return;
        }
        if (this.v2Session?.isReady() &&
            this.admittedCalls >= this.v2Session.admissionWindow()) {
            this.droppedV2Notifications = Math.min(Number.MAX_SAFE_INTEGER, this.droppedV2Notifications + 1);
            return;
        }
        const envelope = {
            id: callId,
            kind: "notify",
            service,
            method,
            payload,
            ts: Date.now(),
        };
        this.sendToNative(envelope, undefined, undefined, undefined, undefined, chunkConfig);
    }
    /**
     * Send a response back to native
     */
    sendResponse(response, chunkConfig = this.chunkConfig) {
        if (this.isDestroyed) {
            return;
        }
        this.sendToNative(response, response.id, undefined, undefined, undefined, chunkConfig);
    }
    /**
     * Setup global message handler
     */
    setupGlobalHandler() {
        if (this.handlerInitialized || typeof window === "undefined") {
            return;
        }
        window.bifrostHandleN2W = (message) => {
            this.handleGlobalMessage(message);
        };
        this.handlerInitialized = true;
    }
    /**
     * Handle incoming messages from native
     */
    async handleGlobalMessage(message) {
        if (this.isDestroyed) {
            return;
        }
        try {
            // Multipart chunk handling — reassemble before dispatching
            if (this.isChunk(message)) {
                const assembled = this.chunkAssembler.addChunk(message);
                if (assembled) {
                    const reassembled = JSON.parse(assembled);
                    if (message.responseId !== undefined &&
                        (!this.isResponse(reassembled) ||
                            reassembled.id !== message.responseId)) {
                        throw new Error("Chunk responseId does not match response id");
                    }
                    await this.handleGlobalMessage(reassembled);
                }
                return;
            }
            if (this.isResponse(message)) {
                this.handleResponse(message);
            }
            else if (this.isCall(message)) {
                await this.handleIncomingCall(message);
            }
            else if (this.isNotification(message)) {
                this.handleIncomingNotification(message);
            }
            else if (this.handshake.isNativeReady(message)) {
                this.handshake.handleNativeReady(message);
            }
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error("[BifrostBridge] Error handling global message:", error);
        }
    }
    /**
     * Handle response messages
     */
    handleResponse(response) {
        const pendingCall = this.pendingCalls.get(response.id);
        if (!pendingCall) {
            return; // Response for unknown call, might be timeout
        }
        this.pendingCalls.delete(response.id);
        if (pendingCall.timeout)
            clearTimeout(pendingCall.timeout);
        if (response.error) {
            const error = new RpcCallError(response.error);
            pendingCall.reject(error);
        }
        else {
            pendingCall.resolve(response.result);
        }
    }
    extendResponseDeadline(chunk) {
        const responseId = chunk.responseId;
        if (!responseId) {
            return;
        }
        const pendingCall = this.pendingCalls.get(responseId);
        if (!pendingCall?.canExtendResponseDeadline) {
            return;
        }
        pendingCall.canExtendResponseDeadline = false;
        pendingCall.deadlineAt += Math.min(chunkTransferAllowanceMs(chunk.total), DEFAULT_TRANSFER_TIMEOUT_MS);
        if (pendingCall.timeout)
            clearTimeout(pendingCall.timeout);
        pendingCall.timeout = setTimeout(() => {
            this.pendingCalls.delete(responseId);
            pendingCall.reject(new Error("RPC call timeout"));
        }, Math.max(0, pendingCall.deadlineAt - Date.now()));
    }
    /**
     * Handle incoming calls from native
     */
    async handleIncomingCall(envelope) {
        if (this.v2Session?.isReady()) {
            const owners = [...this.clients.values()].filter((client) => !client.isDestroyed && client.hasService(envelope.service));
            if (owners.length !== 1) {
                this.sendResponse({
                    id: envelope.id,
                    ok: false,
                    error: {
                        code: owners.length === 0 ? 3001 : 4004,
                        message: owners.length === 0 ? "Service not found" : "Service conflict",
                    },
                    ts: Date.now(),
                });
                return;
            }
            await owners[0].handleIncomingCall(envelope);
            return;
        }
        // Broadcast to all active clients (original behavior)
        for (const client of this.clients.values()) {
            if (!client.isDestroyed) {
                try {
                    await client.handleIncomingCall(envelope);
                }
                catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("[BifrostBridge] Error routing call to client:", error);
                }
            }
        }
    }
    /**
     * Handle incoming notifications from native
     */
    handleIncomingNotification(envelope) {
        if (this.v2Session?.isReady()) {
            const owner = [...this.clients.values()].find((client) => !client.isDestroyed && client.hasService(envelope.service));
            owner?.handleIncomingNotification(envelope);
            return;
        }
        // Broadcast notifications to all active clients
        for (const client of this.clients.values()) {
            if (!client.isDestroyed) {
                try {
                    client.handleIncomingNotification(envelope);
                }
                catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("[BifrostBridge] Error routing notification to client:", error);
                }
            }
        }
    }
    /**
     * Send message to native bridge, chunking if necessary
     */
    sendToNative(message, responseId, onStart, shouldSend, pendingCallId, chunkConfig = this.chunkConfig) {
        if (this.v2Session?.isReady()) {
            try {
                const directLimit = this.v2Session.directLimit();
                onStart?.(directLimit);
                if (shouldSend?.() === false)
                    return;
                if (this.v2Session.send(message) === "not_admitted") {
                    if ("kind" in message && message.kind === "notify") {
                        this.droppedV2Notifications = Math.min(Number.MAX_SAFE_INTEGER, this.droppedV2Notifications + 1);
                        return;
                    }
                    throw new TransportNotAdmittedError();
                }
            }
            catch (error) {
                const failure = error instanceof Error ? error : new Error(String(error));
                const pending = pendingCallId
                    ? this.pendingCalls.get(pendingCallId)
                    : undefined;
                if (pending && pendingCallId) {
                    if (pending.timeout)
                        clearTimeout(pending.timeout);
                    this.pendingCalls.delete(pendingCallId);
                    pending.reject(failure);
                }
            }
            return;
        }
        const configuredThreshold = chunkConfig.chunkThreshold;
        const chunkSender = this.getChunkSender();
        chunkSender.setConfig(chunkConfig);
        chunkSender
            .sendMessage(message, responseId, onStart, shouldSend, () => {
            const nativeBridge = window.bifrostNativeBridge;
            return {
                chunkThreshold: configuredThreshold ??
                    advertisedDirectLimit(nativeBridge) ??
                    256 * 1024,
                send: (msg, serialized) => {
                    if (this.isDestroyed) {
                        throw new Error("BifrostBridge has been destroyed");
                    }
                    if (!nativeBridge) {
                        throw new Error("Native bridge not available");
                    }
                    if (window.bifrostNativeBridge !== nativeBridge) {
                        throw new Error("Native bridge changed during message send");
                    }
                    if (serialized !== undefined &&
                        nativeBridge.sendSerializedToNative) {
                        nativeBridge.sendSerializedToNative(serialized);
                    }
                    else {
                        nativeBridge.sendToNative(msg);
                    }
                },
            };
        })
            .catch((error) => {
            const failure = error instanceof Error ? error : new Error(String(error));
            const pending = pendingCallId
                ? this.pendingCalls.get(pendingCallId)
                : undefined;
            if (pending && pendingCallId) {
                // Surface a transport failure as this call's own rejection. Logging
                // and returning leaves the caller to wait out its full RPC timeout
                // and then report "RPC call timeout" for what was really "the bridge
                // was not there" — the misattribution that makes a dead bridge
                // indistinguishable from a slow one in the field.
                if (pending.timeout)
                    clearTimeout(pending.timeout);
                this.pendingCalls.delete(pendingCallId);
                pending.reject(failure);
                return;
            }
            // Notifications and responses have no caller to reject.
            // eslint-disable-next-line no-console
            console.error("[BifrostBridge] Error sending message to native:", failure);
        });
    }
    /**
     * Tell every registered client the handshake landed. `initialize()` resolving is
     * not a reliable trigger for this: the caller that started the watch may have
     * rejected on its own timeout long before a later caller's extension carried it
     * to completion.
     */
    markClientsReady() {
        for (const client of this.clients.values()) {
            try {
                client.markBridgeReady?.();
            }
            catch (error) {
                // Client code. One faulting client must not stop the rest from learning
                // the bridge is usable.
                // eslint-disable-next-line no-console
                console.error("[BifrostBridge] markBridgeReady threw:", error);
            }
        }
    }
    /**
     * Cleanup resources
     */
    cleanup() {
        for (const waiter of this.admissionWaiters) {
            clearTimeout(waiter.timer);
            waiter.signal?.removeEventListener("abort", waiter.abort);
            waiter.reject(new Error("BifrostBridge destroyed"));
        }
        this.admissionWaiters = [];
        this.admittedCalls = 0;
        // Cancel all pending calls
        for (const [, pendingCall] of this.pendingCalls) {
            if (pendingCall.timeout)
                clearTimeout(pendingCall.timeout);
            pendingCall.reject(new Error("BifrostBridge destroyed"));
        }
        this.pendingCalls.clear();
        // Clear clients
        this.clients.clear();
        // Cleanup chunk assembler
        this.chunkAssembler.destroy();
        // Remove global handler
        if (typeof window !== "undefined") {
            window.bifrostHandleN2W = undefined;
            this.handlerInitialized = false;
        }
        this.handshake.stop();
        this.v2Session?.close();
        this.v2Session = undefined;
        this.isDestroyed = true;
    }
    handleV2Fault() {
        const error = new SessionReplacedError();
        for (const [id, pending] of this.pendingCalls) {
            if (pending.timeout)
                clearTimeout(pending.timeout);
            pending.reject(error);
            this.pendingCalls.delete(id);
        }
        for (const waiter of [...this.admissionWaiters]) {
            this.removeWaiter(waiter);
            waiter.reject(error);
        }
    }
    releaseAdmission() {
        let released = false;
        return () => {
            if (released)
                return;
            released = true;
            if (this.isDestroyed)
                return;
            this.admittedCalls--;
            this.drainAdmissionQueue();
        };
    }
    removeWaiter(waiter) {
        const index = this.admissionWaiters.indexOf(waiter);
        if (index >= 0)
            this.admissionWaiters.splice(index, 1);
        clearTimeout(waiter.timer);
        waiter.signal?.removeEventListener("abort", waiter.abort);
    }
    drainAdmissionQueue() {
        const waiter = this.admissionWaiters[0];
        if (!waiter)
            return;
        const capacity = Math.min(waiter.localLimit, this.v2Session?.admissionWindow() || waiter.localLimit);
        if (this.admittedCalls >= capacity)
            return;
        this.removeWaiter(waiter);
        this.admittedCalls++;
        waiter.resolve(this.releaseAdmission());
    }
    /**
     * Reset bridge for testing (package private)
     */
    static resetForTesting() {
        // @ts-ignore - Reset singleton for testing
        BifrostBridge.instance = undefined;
    }
    /**
     * Destroy the bridge (for testing purposes)
     */
    static destroy() {
        if (BifrostBridge.instance) {
            BifrostBridge.instance.cleanup();
            // @ts-ignore - Reset singleton for testing
            BifrostBridge.instance = undefined;
        }
    }
    /**
     * Check if message is a response
     */
    isResponse(message) {
        return (message &&
            typeof message.id === "string" &&
            (message.result !== undefined || message.error !== undefined));
    }
    /**
     * Check if message is a call
     */
    isCall(message) {
        return message && message.kind === "call" && message.service;
    }
    /**
     * Check if message is a notification
     */
    isNotification(message) {
        return message && message.kind === "notify";
    }
    /**
     * Check if message is a native ready
     */
    /**
     * Check if message is a multipart chunk
     */
    isChunk(message) {
        return message && message.type === "bifrost_chunk";
    }
    /**
     * Get bridge status (for debugging/testing)
     */
    getStatus() {
        return {
            clientCount: this.clients.size,
            pendingCallCount: this.pendingCalls.size,
            isDestroyed: this.isDestroyed,
            handlerInitialized: this.handlerInitialized,
            droppedV2Notifications: this.droppedV2Notifications,
        };
    }
    /**
     * Configure chunking thresholds for large message transport
     */
    setChunkConfig(config) {
        // Merged, not replaced: setting one field used to silently revert the others
        // to their defaults.
        const nextConfig = { ...this.chunkConfig, ...config };
        assertValidChunkConfig(nextConfig);
        this.chunkConfig = nextConfig;
        this.chunkSender?.setConfig(nextConfig);
    }
    /**
     * Get or create the ChunkSender instance
     */
    getChunkSender() {
        if (!this.chunkSender) {
            this.chunkSender = new ChunkSender({
                ...this.chunkConfig,
                send: (msg, serialized) => {
                    // Checked per chunk: the sender yields to the event loop between
                    // batches, so teardown can land mid-transfer. Without this the loop
                    // keeps pushing chunks after the last client has gone, and native
                    // reassembles and executes an RPC whose caller was already rejected.
                    if (this.isDestroyed) {
                        throw new Error("BifrostBridge has been destroyed");
                    }
                    const nativeBridge = window.bifrostNativeBridge;
                    if (!nativeBridge) {
                        throw new Error("Native bridge not available");
                    }
                    if (serialized !== undefined && nativeBridge.sendSerializedToNative) {
                        nativeBridge.sendSerializedToNative(serialized);
                    }
                    else {
                        nativeBridge.sendToNative(msg);
                    }
                },
            });
        }
        return this.chunkSender;
    }
}

/**
 * ProxyBuilder - Fluent API for configuring per-method CallOptions
 *
 * Provides a builder pattern for creating native proxies with method-specific
 * call options, enabling fine-grained control over timeouts and retry strategies.
 *
 * @example
 * ```typescript
 * const userService = bifrost
 *   .createProxyBuilder<UserService>('UserService')
 *   .withDefaultOptions({ timeout: 5000, maxRetries: 2 })
 *   .configureMethod('generateReport', { timeout: 60000, maxRetries: 1 })
 *   .configureMethod('quickCheck', { timeout: 1000, maxRetries: 0 })
 *   .build();
 * ```
 */
/**
 * ProxyBuilder provides a fluent API for configuring native proxies with
 * per-method call options.
 *
 * Supports four-tier option precedence:
 * - Global (BifrostConfig.defaultCallOptions)
 * - Proxy (ProxyBuilder.withDefaultOptions)
 * - Method (ProxyBuilder.configureMethod)
 * - Call (per-call options parameter)
 *
 * Priority: global < proxy < method < call (rightmost wins)
 *
 * @template T - The service interface type
 *
 * @example
 * ```typescript
 * interface UserService {
 *   getProfile(userId: number): Promise<UserProfile>;
 *   generateReport(params: ReportParams): Promise<Report>;
 * }
 *
 * const userService = bifrost
 *   .createProxyBuilder<UserService>('UserService')
 *   .withDefaultOptions({ timeout: 5000 })
 *   .configureMethod('generateReport', { timeout: 60000 })
 *   .build();
 * ```
 */
class ProxyBuilder {
    /**
     * Creates a new ProxyBuilder instance.
     *
     * @param bifrostWeb - The BifrostWeb instance to create the proxy from
     * @param serviceName - Name of the service to create a proxy for
     *
     * @throws Error if service name is invalid
     */
    constructor(bifrostWeb, serviceName) {
        this.bifrostWeb = bifrostWeb;
        this.isBuilt = false;
        this.validateServiceName(serviceName);
        this.config = {
            serviceName,
            methodOptions: {},
            proxyOptions: {},
        };
    }
    /**
     * Set default options for all methods on this proxy.
     *
     * These options override global defaults but can be overridden by
     * method-specific configuration or call-level options.
     *
     * Can be called multiple times; last call wins.
     *
     * @param options - Default call options for all methods
     * @returns This builder instance for method chaining
     *
     * @example
     * ```typescript
     * builder.withDefaultOptions({ timeout: 5000, maxRetries: 2 });
     * ```
     */
    withDefaultOptions(options) {
        this.throwIfBuilt();
        this.validateOptions(options);
        this.config.defaultOptions = options;
        return this;
    }
    /**
     * Configure call options for a specific method.
     *
     * These options override proxy defaults but can be overridden at call time.
     *
     * Can be called multiple times for the same method; last call wins.
     *
     * @param methodName - Name of the method to configure
     * @param options - Call options for this method
     * @returns This builder instance for method chaining
     *
     * @throws Error if method name is invalid
     * @throws Error if options are invalid
     *
     * @example
     * ```typescript
     * builder
     *   .configureMethod('slowMethod', { timeout: 30000, maxRetries: 1 })
     *   .configureMethod('quickMethod', { timeout: 1000, maxRetries: 0 });
     * ```
     */
    configureMethod(methodName, options) {
        this.throwIfBuilt();
        this.validateMethodName(methodName);
        this.validateOptions(options);
        this.config.methodOptions[methodName] = options;
        return this;
    }
    /**
     * Build the configured proxy.
     *
     * Can only be called once per builder instance. After building, the builder
     * is locked and cannot be modified.
     *
     * @returns A typed proxy for the service with configured options
     *
     * @throws Error if builder has already been built
     *
     * @example
     * ```typescript
     * const service = builder
     *   .withDefaultOptions({ timeout: 5000 })
     *   .configureMethod('slowMethod', { timeout: 30000 })
     *   .build();
     * ```
     */
    build() {
        this.throwIfBuilt();
        this.isBuilt = true;
        // Prepare proxy options with method-specific configuration
        const proxyOptions = {
            ...this.config.proxyOptions,
            defaultCallOptions: this.config.defaultOptions,
            methodOptions: this.config.methodOptions,
        };
        // Create proxy using NativeProxyManager
        return this.bifrostWeb.createNativeProxy(this.config.serviceName, proxyOptions);
    }
    /**
     * Get the current configuration for debugging purposes.
     *
     * Returns a readonly copy of the builder's configuration state.
     *
     * @returns Readonly configuration object
     *
     * @example
     * ```typescript
     * const config = builder.getConfiguration();
     * console.log('Service:', config.serviceName);
     * console.log('Default options:', config.defaultOptions);
     * console.log('Method options:', config.methodOptions);
     * ```
     */
    getConfiguration() {
        return {
            serviceName: this.config.serviceName,
            defaultOptions: this.config.defaultOptions,
            methodOptions: { ...this.config.methodOptions },
            proxyOptions: { ...this.config.proxyOptions },
        };
    }
    /**
     * Reset the builder configuration to initial state.
     *
     * Clears all default options and method-specific configurations.
     * Does not reset the service name.
     *
     * @returns This builder instance for method chaining
     *
     * @throws Error if builder has already been built
     *
     * @example
     * ```typescript
     * builder
     *   .withDefaultOptions({ timeout: 5000 })
     *   .configureMethod('slowMethod', { timeout: 30000 })
     *   .reset()  // Clear all configuration
     *   .withDefaultOptions({ timeout: 3000 });
     * ```
     */
    reset() {
        this.throwIfBuilt();
        this.config.defaultOptions = undefined;
        this.config.methodOptions = {};
        return this;
    }
    /**
     * Validate service name format.
     *
     * Service names must:
     * - Not be empty
     * - Start with a letter
     * - Contain only letters, numbers, and underscores
     *
     * @param serviceName - Service name to validate
     * @throws Error if service name is invalid
     */
    validateServiceName(serviceName) {
        if (!serviceName || typeof serviceName !== "string") {
            throw new Error(`Invalid service name: ${serviceName}`);
        }
        const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;
        if (!validNamePattern.test(serviceName)) {
            throw new Error(`Invalid service name: ${serviceName}`);
        }
    }
    /**
     * Validate method name format.
     *
     * Method names must:
     * - Not be empty
     * - Start with a letter
     * - Contain only letters, numbers, and underscores
     *
     * @param methodName - Method name to validate
     * @throws Error if method name is invalid
     */
    validateMethodName(methodName) {
        if (!methodName || typeof methodName !== "string") {
            throw new Error(`Invalid method name: ${methodName}`);
        }
        const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;
        if (!validNamePattern.test(methodName)) {
            throw new Error(`Invalid method name: ${methodName}`);
        }
    }
    /**
     * Validate CallOptions structure.
     *
     * Ensures that:
     * - timeout is positive if specified
     * - maxRetries is non-negative if specified
     *
     * @param options - Options to validate
     * @throws Error if options are invalid
     */
    validateOptions(options) {
        if (options.timeout !== undefined) {
            if (typeof options.timeout !== "number" || options.timeout < 0) {
                throw new Error(`Invalid timeout: ${options.timeout}. Must be a positive number.`);
            }
        }
        if (options.maxRetries !== undefined) {
            if (typeof options.maxRetries !== "number" || options.maxRetries < 0) {
                throw new Error(`Invalid maxRetries: ${options.maxRetries}. Must be a non-negative number.`);
            }
        }
    }
    /**
     * Throw error if builder has already been built.
     *
     * @throws Error if builder has already been built
     */
    throwIfBuilt() {
        if (this.isBuilt) {
            throw new Error("Builder has already been built. Create a new builder to configure another proxy.");
        }
    }
}

/**
 * BifrostWeb - Main RPC framework class
 * TypeScript-first bidirectional WebView RPC framework
 */
/**
 * BifrostWeb - Main RPC framework class for bidirectional WebView communication
 *
 * Enables seamless communication between web content and native applications
 * with full TypeScript support, error handling, and advanced features like
 * interceptors, and connection management.
 *
 * @example
 * ```typescript
 * const bifrost = new BifrostWeb({
 *   readyTimeout: 15000,
 *   maxInflightCalls: 32
 * });
 *
 * await bifrost.initialize();
 * const result = await bifrost.call('UserService', 'getProfile', { userId: 123 });
 * ```
 */
class BifrostWeb {
    /**
     * Creates a new BifrostWeb instance
     *
     * @param config - Configuration options for the RPC framework
     * @param config.readyTimeout - Deadline for the native handshake. Defaults to
     * DEFAULT_READY_TIMEOUT_MS, which is sized to the host's own page deadline.
     * @param config.maxInflightCalls - Maximum concurrent RPC calls (default: 32)
     * @param config.retryConfig - Retry configuration for failed calls.
     * `retryConfig.retryableErrors` is ignored: a native error response means
     * the call was already admitted and may have changed state, so this
     * client never replays it on the strength of an error class.
     * @param config.enableDebug - Enable debug logging (default: false)
     * @param config.interceptors - Default interceptors to register
     * @param config.onReady - Callback when native bridge is ready
     * @param config.onError - Callback for error handling
     */
    constructor(config = {}) {
        this.chunkConfig = {};
        this.callIdCounter = 0;
        this.lifetime = new AbortController();
        this.isDestroyed = false;
        // Generate unique client ID
        this.clientId = this.generateClientId();
        // Set default configuration
        this.config = {
            readyTimeout: config.readyTimeout ?? DEFAULT_READY_TIMEOUT_MS,
            maxInflightCalls: config.maxInflightCalls ?? 32,
            retryConfig: config.retryConfig ?? {
                maxRetries: 3,
                retryDelay: 1000,
                retryBackoff: 2,
            },
            enableDebug: config.enableDebug ?? false,
            onReady: config.onReady,
            onError: config.onError,
        };
        // Store global default call options
        this.defaultCallOptions = config.defaultCallOptions;
        // Initialize core components
        this.connectionState = new ConnectionState(this.config.readyTimeout);
        this.retryManager = new RetryManager({
            maxRetries: this.config.retryConfig?.maxRetries ?? 3,
            retryDelay: this.config.retryConfig?.retryDelay ?? 1000,
            retryBackoff: this.config.retryConfig?.retryBackoff ?? 2,
            // A native response means the operation was admitted and may already
            // have changed state. Bifrost calls only replay explicit refusals.
            retryableErrors: [],
        });
        this.serviceRegistry = new ServiceRegistry();
        this.proxyManager = new NativeProxyManager(this);
        this.interceptorChain = new InterceptorChain();
        // Register default interceptors from config
        if (config.interceptors) {
            for (const interceptor of config.interceptors) {
                this.interceptorChain.addInterceptor(interceptor);
            }
        }
    }
    /**
     * Get fresh bridge instance (handles reconnection after destruction)
     */
    getBridge() {
        return BifrostBridge.getInstance();
    }
    /**
     * Initialize the BifrostWeb framework
     *
     * Sets up the connection to the native bridge, configures message handlers,
     * and waits for the native side to be ready for communication.
     *
     * @throws {Error} If native bridge is not available or ready timeout is exceeded
     *
     * @example
     * ```typescript
     * const bifrost = new BifrostWeb();
     * await bifrost.initialize();
     * // Now ready to make RPC calls
     * ```
     */
    async initialize() {
        if (this.isDestroyed) {
            throw new Error("BifrostWeb has been destroyed");
        }
        // Get bridge and register this client
        const bridge = this.getBridge();
        bridge.registerClient(this);
        // Initialize the bridge (handles native bridge setup and global handler)
        await bridge.initialize(this.config.readyTimeout);
        this.connectionState.setReady();
    }
    /**
     * The shared bridge handshake completed (see IBifrostClient.markBridgeReady).
     */
    markBridgeReady() {
        if (!this.isDestroyed) {
            this.connectionState.setReady();
        }
    }
    /**
     * Register a service for native-to-web calls
     *
     * Registers a JavaScript object as a service that can be called from the native side.
     * All methods on the service object become callable via RPC.
     *
     * @param serviceName - Unique name for the service
     * @param serviceImpl - Object containing methods to expose to native side
     *
     * @throws {Error} If service name is already registered or invalid
     *
     * @example
     * ```typescript
     * const webService = {
     *   showNotification: (message: string) => {
     *     new Notification(message);
     *     return { displayed: true };
     *   },
     *   validateForm: async (data: any) => {
     *     return { valid: true, errors: [] };
     *   }
     * };
     *
     * bifrost.registerService('WebUIService', webService);
     * ```
     */
    registerService(serviceName, serviceImpl) {
        this.throwIfDestroyed();
        this.serviceRegistry.register(serviceName, serviceImpl);
        // Keep the simple broadcast behavior for now - no need to register with bridge
    }
    /**
     * Unregister a service
     */
    unregisterService(serviceName) {
        this.throwIfDestroyed();
        this.serviceRegistry.unregister(serviceName);
    }
    /**
     * Check if this client has a specific service (required by IBifrostClient)
     */
    hasService(serviceName) {
        return this.serviceRegistry.listServices().includes(serviceName);
    }
    /**
     * Call a native method with safe transport retry logic
     *
     * Sends an RPC call to the native side and waits for the response.
     * Retries calls explicitly refused before admission and handles timeouts.
     *
     * @param service - Name of the native service to call
     * @param method - Method name on the service
     * @param payload - Parameters to send with the call (default: {})
     * @param options - Call options including timeout and retries
     * @returns Promise that resolves with the method result
     *
     * @throws {RpcCallError} If the call fails or times out
     *
     * @example
     * ```typescript
     * // Simple call
     * const result = await bifrost.call('UserService', 'getProfile', { userId: 123 });
     *
     * // Call with options
     * const result = await bifrost.call('FileService', 'upload', { name: 'file.pdf' }, {
     *   timeout: 30000,
     * });
     * ```
     */
    async call(service, method, payload = {}, options) {
        this.throwIfDestroyed();
        // Merge global + call-level options (no proxy/method involved in direct calls)
        const finalOptions = this.mergeCallOptions(undefined, undefined, options);
        const deadlineAt = Date.now() + (finalOptions?.timeout ?? 5000);
        await this.waitUntilReady(deadlineAt, finalOptions?.signal);
        await this.getBridge().recoverV2(deadlineAt);
        const release = await this.getBridge().admitCall(deadlineAt, this.config.maxInflightCalls, finalOptions?.signal);
        const applicationCallId = this.generateCallId();
        try {
            return await this.retryManager.withRetry(async () => {
                return this.performCall(service, method, payload, finalOptions, applicationCallId, deadlineAt);
            });
        }
        finally {
            release();
        }
    }
    /**
     * Request one native resource using an explicit representation preference.
     *
     * The returned value is always the tagged V2 union. Legacy callers should
     * continue using their existing untagged Base64 service contracts.
     */
    async requestResource(request, options) {
        const validated = validateResourceRequest(request);
        const result = await this.call(BifrostWeb.RESOURCE_SERVICE, "resolve", validated, options);
        return decodeResourceRepresentation(result);
    }
    /**
     * Send notification to native (fire-and-forget)
     */
    notify(service, method, payload = {}) {
        this.throwIfDestroyed();
        const callId = this.generateCallId();
        const envelope = {
            id: callId,
            kind: "notify",
            service,
            method,
            payload,
            ts: Date.now(),
        };
        const chunkConfig = this.chunkConfig;
        // Process through outbound interceptor chain (async but not awaited for fire-and-forget)
        this.interceptorChain
            .process("outbound", envelope, async () => {
            this.getBridge().sendNotification(service, method, payload, callId, chunkConfig);
            return undefined;
        })
            .catch((error) => {
            // Log interceptor errors but don't throw since notify is fire-and-forget
            if (this.config.enableDebug) {
                console.error("Interceptor error in notify:", error); // eslint-disable-line no-console
            }
        });
    }
    /**
     * Create a typed native service proxy with caching and validation
     *
     * Creates a strongly-typed proxy object that provides direct method access
     * to native services. Method calls are automatically converted to RPC calls
     * with full TypeScript support and IntelliSense.
     *
     * @template T - TypeScript interface defining the service methods
     * @param serviceName - Name of the native service to proxy
     * @param options - Proxy configuration options
     * @returns Typed proxy object with methods that call the native service
     *
     * @example
     * ```typescript
     * interface UserService {
     *   getProfile(userId: number): Promise<UserProfile>;
     *   updateProfile(data: Partial<UserProfile>): Promise<void>;
     * }
     *
     * const userService = bifrost.createNativeProxy<UserService>('UserService');
     * const profile = await userService.getProfile(123);
     * await userService.updateProfile({ name: 'New Name' });
     * ```
     */
    createNativeProxy(serviceName, options) {
        this.throwIfDestroyed();
        return this.proxyManager.createProxy(serviceName, options);
    }
    /**
     * Create a ProxyBuilder for configuring per-method call options
     *
     * Provides a fluent API for configuring different CallOptions (timeout, maxRetries)
     * for individual methods on a proxy. Supports four-tier option precedence:
     * global < proxy < method < call.
     *
     * @template T - TypeScript interface defining the service methods
     * @param serviceName - Name of the native service to proxy
     * @returns ProxyBuilder instance for configuring the proxy
     *
     * @example
     * ```typescript
     * interface UserService {
     *   quickCheck(): Promise<boolean>;
     *   generateReport(params: any): Promise<Report>;
     * }
     *
     * const userService = bifrost
     *   .createProxyBuilder<UserService>('UserService')
     *   .withDefaultOptions({ timeout: 5000 })
     *   .configureMethod('generateReport', { timeout: 60000, maxRetries: 1 })
     *   .build();
     *
     * const isReady = await userService.quickCheck(); // Uses 5000ms timeout
     * const report = await userService.generateReport({}); // Uses 60000ms timeout
     * ```
     */
    createProxyBuilder(serviceName) {
        this.throwIfDestroyed();
        return new ProxyBuilder(this, serviceName);
    }
    /**
     * Create a native service proxy (legacy method for backward compatibility)
     */
    createLegacyNativeProxy(serviceName) {
        this.throwIfDestroyed();
        return {
            call: (method, payload, options) => {
                return this.call(serviceName, method, payload, options);
            },
            notify: (method, payload) => {
                this.notify(serviceName, method, payload);
            },
        };
    }
    /**
     * List all registered services
     */
    listServices() {
        this.throwIfDestroyed();
        return this.serviceRegistry.listServices();
    }
    /**
     * Get service descriptor
     */
    getServiceDescriptor(serviceName) {
        this.throwIfDestroyed();
        return this.serviceRegistry.getServiceDescriptor(serviceName);
    }
    /**
     * List all cached native proxies
     */
    listCachedProxies() {
        this.throwIfDestroyed();
        return this.proxyManager.listCachedProxies();
    }
    /**
     * Clear specific proxy from cache
     */
    clearProxyCache(serviceName) {
        this.throwIfDestroyed();
        this.proxyManager.clearProxy(serviceName);
    }
    /**
     * Clear all proxy caches
     */
    clearAllProxyCaches() {
        this.throwIfDestroyed();
        this.proxyManager.clearAllProxies();
    }
    /**
     * Add an interceptor to the chain
     *
     * Interceptors are executed in order based on their `order` property.
     * Lower order values execute first.
     *
     * @param interceptor - The interceptor to add
     *
     * @example
     * ```typescript
     * import { LoggingInterceptor } from '@seekrtech/bifrost-web';
     *
     * const loggingInterceptor = new LoggingInterceptor();
     * bifrost.addInterceptor(loggingInterceptor);
     * ```
     */
    addInterceptor(interceptor) {
        this.throwIfDestroyed();
        this.interceptorChain.addInterceptor(interceptor);
    }
    /**
     * Remove an interceptor from the chain by name
     *
     * @param name - Name of the interceptor to remove
     *
     * @example
     * ```typescript
     * bifrost.removeInterceptor('LoggingInterceptor');
     * ```
     */
    removeInterceptor(name) {
        this.throwIfDestroyed();
        this.interceptorChain.removeInterceptor(name);
    }
    /**
     * Configure chunking thresholds for large message transport
     */
    setChunkConfig(config) {
        // Every other public method guards this; without it, calling setChunkConfig
        // on a destroyed client resurrects the bridge singleton and reinstalls the
        // global message handler.
        this.throwIfDestroyed();
        const nextConfig = { ...this.chunkConfig, ...config };
        assertValidChunkConfig(nextConfig);
        this.chunkConfig = nextConfig;
    }
    /**
     * Clear all interceptors from the chain
     */
    clearInterceptors() {
        this.throwIfDestroyed();
        this.interceptorChain.clearInterceptors();
    }
    /**
     * Get all registered interceptors
     */
    getInterceptors() {
        this.throwIfDestroyed();
        return this.interceptorChain.getInterceptors();
    }
    /**
     * Get global default call options (used by NativeProxyManager)
     *
     * @returns Global default CallOptions or undefined
     */
    getDefaultCallOptions() {
        return this.defaultCallOptions;
    }
    /**
     * Merge call options from multiple levels
     * Priority: global < proxy < method < call (rightmost wins)
     *
     * @param proxyOptions - Proxy-level default options
     * @param methodOptions - Method-specific options
     * @param callOptions - Call-level options
     * @returns Merged CallOptions or undefined if all inputs are undefined
     */
    mergeCallOptions(proxyOptions, methodOptions, callOptions) {
        const merged = {
            ...this.defaultCallOptions,
            ...proxyOptions,
            ...methodOptions,
            ...callOptions,
        };
        // Return undefined if result is empty object (preserves backward compatibility)
        return Object.keys(merged).length === 0 ? undefined : merged;
    }
    /**
     * Destroy the framework and cleanup resources
     */
    destroy() {
        if (this.isDestroyed) {
            return;
        }
        this.lifetime.abort();
        // Unregister from bridge (if bridge still exists)
        try {
            this.getBridge().unregisterClient(this.clientId);
        }
        catch (error) {
            // Bridge might be destroyed already, that's okay
        }
        // Cleanup proxy manager
        this.proxyManager.destroy();
        this.isDestroyed = true;
    }
    /**
     * Perform actual RPC call
     */
    async performCall(service, method, payload, options, applicationCallId, deadlineAt) {
        const callDeadlineAt = deadlineAt ?? Date.now() + (options?.timeout ?? 5000);
        this.assertCallActive(callDeadlineAt, options?.signal);
        await this.connectionState.ensureReady();
        this.assertCallActive(callDeadlineAt, options?.signal);
        const callId = this.generateCallId();
        const envelope = {
            id: callId,
            kind: "call",
            service,
            method,
            payload,
            ts: Date.now(),
            applicationCallId,
            idempotencyKey: options?.idempotencyKey,
            deadlineAt: callDeadlineAt,
        };
        const chunkConfig = this.chunkConfig;
        // Process through outbound interceptor chain
        return this.interceptorChain.process("outbound", envelope, async () => {
            this.assertCallActive(callDeadlineAt, options?.signal);
            const remainingOptions = options?.timeout !== undefined
                ? { ...options, timeout: Math.max(0, callDeadlineAt - Date.now()) }
                : options;
            // Use bridge to send call with the (potentially modified) envelope
            try {
                return await this.getBridge().sendCall(this.clientId, envelope.id, envelope.service, envelope.method, envelope.payload, remainingOptions, envelope, chunkConfig);
            }
            catch (error) {
                if (error instanceof TransportNotAdmittedError) {
                    // Deliberate: tag the error with a call-scoped retry boundary so
                    // RetryManager can wait on this call's own deadline/signal instead
                    // of a plain sleep, without either module importing the other.
                    Object.defineProperty(error, CALL_RETRY_BOUNDARY, {
                        value: {
                            waitBeforeRetry: (delay) => this.waitBeforeCallRetry(delay, callDeadlineAt, options?.signal),
                        },
                    });
                }
                throw error;
            }
        });
    }
    assertCallActive(deadlineAt, signal) {
        this.throwIfDestroyed();
        if (this.lifetime.signal.aborted)
            throw new Error("BifrostWeb has been destroyed");
        if (signal?.aborted)
            throw new CallCancelledError();
        if (Date.now() >= deadlineAt)
            throw new CallDeadlineExceededError();
    }
    waitBeforeCallRetry(delay, deadlineAt, signal) {
        this.assertCallActive(deadlineAt, signal);
        return new Promise((resolve, reject) => {
            const remaining = Math.max(0, deadlineAt - Date.now());
            const timer = setTimeout(() => {
                try {
                    this.assertCallActive(deadlineAt, signal);
                    finish(resolve);
                }
                catch (error) {
                    finish(() => reject(error));
                }
            }, Math.min(delay, remaining));
            const cancel = () => finish(() => reject(new CallCancelledError()));
            const dispose = () => finish(() => reject(new Error("BifrostWeb has been destroyed")));
            const finish = (settle) => {
                clearTimeout(timer);
                signal?.removeEventListener("abort", cancel);
                this.lifetime.signal.removeEventListener("abort", dispose);
                settle();
            };
            signal?.addEventListener("abort", cancel, { once: true });
            this.lifetime.signal.addEventListener("abort", dispose, { once: true });
        });
    }
    async waitUntilReady(deadlineAt, signal) {
        if (signal?.aborted)
            throw new CallCancelledError();
        await new Promise((resolve, reject) => {
            const timer = setTimeout(() => finish(() => reject(new CallDeadlineExceededError())), Math.max(0, deadlineAt - Date.now()));
            const abort = () => finish(() => reject(new CallCancelledError()));
            const finish = (settle) => {
                clearTimeout(timer);
                signal?.removeEventListener("abort", abort);
                settle();
            };
            signal?.addEventListener("abort", abort, { once: true });
            this.connectionState.ensureReady().then(() => finish(resolve), (error) => finish(() => reject(error)));
        });
    }
    /**
     * Handle incoming call from native (required by IBifrostClient)
     */
    async handleIncomingCall(envelope) {
        // Process through inbound interceptor chain
        await this.interceptorChain.process("inbound", envelope, async () => {
            let result = null;
            let error = null;
            try {
                result = await this.serviceRegistry.invoke(envelope.service, envelope.method, envelope.payload);
            }
            catch (err) {
                if (err instanceof RpcCallError) {
                    error = {
                        code: err.code,
                        message: err.message,
                    };
                }
                else {
                    error = {
                        code: ErrorCode.INTERNAL_ERROR,
                        message: err instanceof Error ? err.message : "Unknown error",
                    };
                }
            }
            // Create response
            const response = {
                id: envelope.id,
                ok: error === null,
                result,
                error,
                ts: Date.now(),
            };
            const chunkConfig = this.chunkConfig;
            // Process response through outbound interceptor chain
            await this.interceptorChain.process("outbound", response, async () => {
                // Use bridge to send response back
                this.getBridge().sendResponse(response, chunkConfig);
                return undefined;
            });
            return undefined;
        });
    }
    /**
     * Handle incoming notification from native (required by IBifrostClient)
     */
    handleIncomingNotification(envelope) {
        // Process through inbound interceptor chain (async but not awaited for fire-and-forget)
        this.interceptorChain
            .process("inbound", envelope, async () => {
            await this.serviceRegistry.invoke(envelope.service, envelope.method, envelope.payload);
        })
            .catch((error) => {
            // Log interceptor errors but don't throw since notifications are fire-and-forget
            if (this.config.enableDebug) {
                console.error("Interceptor error in handleIncomingNotification:", error); // eslint-disable-line no-console
            }
        });
    }
    /**
     * Generate unique client ID
     */
    generateClientId() {
        return `client_${Math.random().toString(36).substring(2)}_${Date.now()}`;
    }
    /**
     * Generate unique call ID
     */
    generateCallId() {
        return `${this.clientId}_${++this.callIdCounter}_${Date.now()}`;
    }
    /**
     * Throw error if framework has been destroyed
     */
    throwIfDestroyed() {
        if (this.isDestroyed) {
            throw new Error("BifrostWeb has been destroyed");
        }
    }
}
/** Reserved native service used by the explicit V2 resource API. */
BifrostWeb.RESOURCE_SERVICE = "BifrostResourceService";

export { AuthInterceptor, BifrostWeb, CallCancelledError, CallDeadlineExceededError, ChunkAssembler, ChunkSender, CircuitBreakerInterceptor, CircuitBreakerState, ConnectionState, DeliveryUnknownError, ErrorCategory, ErrorCode, InterceptorChain, InvalidResourceRepresentationError, LogLevel, LoggingInterceptor, NativeProxyManager, OverloadedError, PayloadTooLargeError, RequestInterceptor, RequestQueueInterceptor, ResponseInterceptor, RetryManager, RpcCallError, ServiceRegistry, ServiceValidator, SessionReplacedError, TimingInterceptor, TransportNotAdmittedError, UnsupportedResourceRepresentationError, ValidationInterceptor, bifrostImageToDataUrl, decodeLegacyBase64Resource, decodeResourceRepresentation, splitIntoChunks, validateResourceRequest };
//# sourceMappingURL=index.esm.js.map
